import Phaser from 'phaser';
import { HorseSprite, TimeOfDay, HorseGender, unlockPaint, isPaintUnlocked } from '../objects/HorseSprite';
import { Card } from '../objects/Card';
import { ResourceDisplay } from '../objects/ResourceDisplay';
import { AudioManager } from '../managers/AudioManager';
import { GAME_WIDTH, GAME_HEIGHT } from './BootScene';
import { 
  pryorStarterDeck, 
  CardData, 
  GameResources,
  getRandomCardDrop,
  legendaryCards,
  rareCards,
  sillyEventCards,
  seasonalEventCards,
  choiceEvents,
  ChoiceEventData
} from '../data/cards';

// Game Constants
const AML_LOW = 90;
const AML_HIGH = 120;

// UI Colors - Matching Pryor Mountain backgrounds
const COLORS = {
  bone: 0xD2CFBF,        // Light tan/bone
  cornflower: 0x6B94E4,  // Sky blue
  oliveWood: 0x8B5A2B,   // Brown wood
  deepForest: 0x163F29,  // Dark green
  cream: 0xE8E4D8,       // Soft cream
  textDark: 0x2F3D2F,    // Dark forest text
  success: 0x4A7C59,     // Muted green
  danger: 0xA65D57,      // Muted red
  gold: 0xC9A227,        // Warm gold
};

// Game Resources (mutable state) - v3.0 with new mechanics
export const RESOURCES: GameResources = {
  population: 25,
  forage: 70,
  water: 80,
  funds: 10000,
  year: 1990,
  foalRate: 0.10,
  diversity: 50,
  // NEW MECHANICS
  reputation: 50,    // Public support (affects donations)
  herdHealth: 75,    // Overall herd condition
  blmRelations: 50,  // Government cooperation
};

// Game end year (20 year playtime)
export const END_YEAR = 2010;

// Action Points system for pacing
export const ACTION_SYSTEM = {
  actionsPerYear: 2,      // Actions available each year
  actionsRemaining: 2,    // Current actions left
  breedingCooldown: 0,    // Years until breeding available again
  cardsPlayedThisYear: 0, // Track cards played (max 8 per year)
  maxCardsPerYear: 8,     // Maximum cards that can be played per year
  boughtCardsThisYear: false, // Can only buy cards once per year
};

// Game state for deck evolution
export interface GameState {
  currentHMA: string;
  unlockedHMAs: string[];
  persistentDeck: CardData[];
  discardPile: CardData[];
  achievements: string[];
  rangerName: string;
  journalEntries: string[];
  totalFoalsBorn: number;
  yearsPlayed: number;
}

export const GAME_STATE: GameState = {
  currentHMA: 'pryor',
  unlockedHMAs: ['pryor'],
  persistentDeck: [],
  discardPile: [],
  achievements: [],
  rangerName: 'Ranger',
  journalEntries: [],
  totalFoalsBorn: 0,
  yearsPlayed: 0
};

export class GameScene extends Phaser.Scene {
  private horses: HorseSprite[] = [];
  private hand: Card[] = [];
  private deck: CardData[] = [];
  private resourceDisplay!: ResourceDisplay;
  private endYearButton!: Phaser.GameObjects.Container;
  private eventText!: Phaser.GameObjects.Text;
  private handContainer!: Phaser.GameObjects.Container;
  private journalContainer!: Phaser.GameObjects.Container;
  private choiceModal!: Phaser.GameObjects.Container;
  private audioControlsContainer!: Phaser.GameObjects.Container;
  private season: 'spring' | 'summer' | 'autumn' | 'winter' = 'spring';
  
  // Day/Night cycle
  private currentTimeOfDay: TimeOfDay = 'morning';
  private backgroundImage!: Phaser.GameObjects.Image;
  private nightOverlay!: Phaser.GameObjects.Rectangle;
  private morningOverlay!: Phaser.GameObjects.Rectangle;
  
  // Audio
  private audioManager!: AudioManager;
  
  // Interactive elements
  private saltBlock?: Phaser.GameObjects.Container;
  private actionPointsText!: Phaser.GameObjects.Text;
  private selectedHorse?: HorseSprite;
  
  // Weather & celestial
  private celestialBody?: Phaser.GameObjects.Container;
  private weatherParticles: Phaser.GameObjects.Text[] = [];
  private ambientWeatherEvent?: Phaser.Time.TimerEvent;
  
  // Fire regrowth bonus
  private fireRegrowthBonus: number = 0;
  
  // Action system - progress bars and cooldowns
  private actionProgressBar?: Phaser.GameObjects.Container;
  private isActionInProgress: boolean = false;
  private activeFires: Phaser.GameObjects.Text[] = [];
  private crisisCountThisYear: number = 0;
  
  // Action buttons
  private partyButton?: Phaser.GameObjects.Container;
  private waterTroughButton?: Phaser.GameObjects.Container;
  private scoutButton?: Phaser.GameObjects.Container;
  
  // Year counter display
  private yearCounterText?: Phaser.GameObjects.Text;
  
  // Adopt button
  private adoptButton?: Phaser.GameObjects.Container;
  
  // Burros (using Image sprite)
  private burros: Phaser.GameObjects.Image[] = [];
  
  // Buy cards button
  private buyCardsButton?: Phaser.GameObjects.Container;
  
  // Action buttons container
  private actionButtonsContainer?: Phaser.GameObjects.Container;
  
  // Pond ducks
  private duckContainer?: Phaser.GameObjects.Container;
  
  // Alert box
  private alertBox?: Phaser.GameObjects.Container;
  
  // Halter breaking system
  private horseClickCounts: Map<HorseSprite, number> = new Map();
  private halterBreakingHorse?: HorseSprite;
  private halterProgressBar?: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // Initialize audio manager
    this.audioManager = new AudioManager(this);
    
    // Determine initial time of day based on year
    this.updateTimeOfDay();
    
    // Background - starts with appropriate time of day
    const bgKey = this.getBackgroundKey();
    this.backgroundImage = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, bgKey);
    this.backgroundImage.setDepth(-10); // Behind everything
    
    // Scale background to fit 900x700 if needed
    const bgTexture = this.textures.get(bgKey);
    if (bgTexture && bgTexture.source[0]) {
      const scaleX = GAME_WIDTH / bgTexture.source[0].width;
      const scaleY = GAME_HEIGHT / bgTexture.source[0].height;
      this.backgroundImage.setScale(Math.max(scaleX, scaleY));
    }
    
    // Time-of-day color overlays (subtle atmospheric tinting)
    this.morningOverlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xFFD700, 0)
      .setDepth(-5);
    this.nightOverlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000033, 0)
      .setDepth(-5);
    
    // Apply initial overlay based on time
    this.applyTimeOverlay();
    
    // Create pond ducks
    this.createPondDucks();
    
    // Sun/Moon indicator
    this.createCelestialBody();
    
    // Weather effects
    this.startWeatherEffects();
    
    console.log(`Starting with ${this.currentTimeOfDay} - Background: ${bgKey}`);
    
    // Play zone - shows card description when hovering
    this.createPlayZone();

    // Initialize deck
    this.initializeDeck();
    
    // Spawn horses
    this.spawnHorses();
    
    // Resource display - moved down to not overlap play zone
    this.resourceDisplay = new ResourceDisplay(this, 10, 140);
    
    // Hand container
    this.handContainer = this.add.container(0, 0);
    
    // Journal
    this.createJournal();
    
    // Draw initial hand
    this.drawHand();
    
    // End Year button
    this.createEndYearButton();
    
    // Action points display
    this.createActionPointsDisplay();
    
    // Interactive elements
    this.createInteractiveElements();
    
    // Audio controls (top-right)
    this.createAudioControls();
    
    // Event text - fancy styling with shadow
    this.eventText = this.add.text(GAME_WIDTH / 2, 200, '', {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '24px',
      fontStyle: 'bold',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 4,
      shadow: { offsetX: 2, offsetY: 2, color: '#000000', blur: 4, fill: true }
    }).setOrigin(0.5).setAlpha(0).setDepth(1500);

    // Choice modal
    this.choiceModal = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2).setVisible(false);
    
    // Year counter at bottom
    this.createYearCounter();

    // Event listeners
    this.events.on('cardPlayed', (card: CardData) => {
      // Block further actions briefly
      if (this.isActionInProgress) return;
      
      // Check if max cards played this year
      if (ACTION_SYSTEM.cardsPlayedThisYear >= ACTION_SYSTEM.maxCardsPerYear) {
        this.showEvent('⚠️ Max cards played this year!');
        return;
      }
      
      // Track cards played
      ACTION_SYSTEM.cardsPlayedThisYear++;
      
      // Play card sound
      this.audioManager.playSFX('card_use');
      
      // Particle effects at drop zone
      this.createCardPlayParticles(GAME_WIDTH / 2, 85, card.rarity);
      
      // Sound effects based on card type
      if (card.type === 'horse') {
        // Horse whinny for horse cards (10% chance to avoid spam)
        if (Math.random() < 0.1) {
          this.audioManager.playSFX('horse');
        }
      }
      
      // Coin sound for money-related cards
      if (card.cost && card.cost > 0) {
        this.audioManager.playSFX('coin');
      }
      if (card.title.toLowerCase().includes('donation') || 
          card.title.toLowerCase().includes('fundrais') ||
          card.title.toLowerCase().includes('grant') ||
          card.title.toLowerCase().includes('tour')) {
        this.audioManager.playSFX('coin');
      }
      
      // Check for PZP/Farrier/Vet actions
      if (card.id.includes('pzp') || card.id.includes('vaccine') || 
          card.id.includes('farrier') || card.id.includes('vet')) {
        this.audioManager.playSFX('farrier');
      }
      
      // Brief progress bar for card play
      this.startActionWithProgress(`🃏 ${card.title}`, 1200, () => {
        // ============ CARD EFFECT TRIGGERS ============
        this.triggerCardEffects(card);
        
        // Spawn horse matching card if it's a horse card
        this.spawnHorseFromCard(card);
        
        // DON'T call updateHorseCount here - it removes spawned horses!
        this.resourceDisplay.update();
      });
      
      // Remove the played card from hand array
      // Find and remove the FIRST matching card (not all copies)
      const cardIndex = this.hand.findIndex(c => 
        (c as any).cardData?.id === card.id && (c as any).cardData?.title === card.title
      );
      if (cardIndex !== -1) {
        this.hand.splice(cardIndex, 1);
      }
      
      this.rearrangeHand();
      GAME_STATE.discardPile.push(card);
      this.addJournalEntry(`Played: ${card.title}`);
    });

    // Card pickup sound - subtle
    this.events.on('cardPickup', () => {
      // No sound on pickup - only on play
    });

    // Title banner removed - cleaner look

    // Start music
    this.startMusic();
    
    // Fade in
    this.cameras.main.fadeIn(500);
  }

  // ============ PLAY ZONE ============
  
  private playZoneDescText!: Phaser.GameObjects.Text;
  
  private createPlayZone(): void {
    // Elegant drop zone - centered in play area
    const playZoneBg = this.add.rectangle(GAME_WIDTH / 2, 60, GAME_WIDTH - 40, 70, COLORS.deepForest, 0.75);
    playZoneBg.setStrokeStyle(2, COLORS.bone, 0.6);
    playZoneBg.setDepth(5);
    
    // Title with subtle styling
    const playZoneTitle = this.add.text(GAME_WIDTH / 2, 40, '▼ DROP CARDS HERE ▼', {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#D2CFBF'
    }).setOrigin(0.5).setDepth(6);
    
    // Description text - BIGGER for readability
    this.playZoneDescText = this.add.text(GAME_WIDTH / 2, 72, 'Drag a card from your hand to play it', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      color: '#B0B0A0',
      align: 'center',
      wordWrap: { width: GAME_WIDTH - 60 }
    }).setOrigin(0.5).setDepth(6);
    
    // Subtle pulse on border
    this.tweens.add({
      targets: playZoneBg,
      alpha: 0.55,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }
  
  // Particle effect when card is played - smooth burst
  private createCardPlayParticles(x: number, y: number, rarity: string): void {
    const sparkles = ['✨', '⭐', '💫', '✦', '◆'];
    const count = rarity === 'legendary' ? 16 : rarity === 'rare' ? 10 : 6;
    const baseSize = rarity === 'legendary' ? 20 : rarity === 'rare' ? 16 : 12;
    
    // Stagger particle creation for smoother effect
    for (let i = 0; i < count; i++) {
      this.time.delayedCall(i * 30, () => {
        const angle = (i / count) * Math.PI * 2 + Math.random() * 0.5;
        const distance = Phaser.Math.Between(30, 80);
        const startX = x + Math.cos(angle) * 10;
        const startY = y + Math.sin(angle) * 5;
        const endX = x + Math.cos(angle) * distance;
        const endY = y - Phaser.Math.Between(40, 100);
        
        const particle = this.add.text(startX, startY, Phaser.Math.RND.pick(sparkles), { 
          fontSize: `${baseSize + Phaser.Math.Between(-4, 4)}px` 
        }).setDepth(1000).setAlpha(0.9);
        
        this.tweens.add({
          targets: particle,
          x: endX,
          y: endY,
          alpha: 0,
          scale: { from: 1.2, to: 0.2 },
          rotation: Phaser.Math.Between(-1, 1),
          duration: Phaser.Math.Between(800, 1400),
          ease: 'Quad.easeOut',
          onComplete: () => particle.destroy()
        });
      });
    }
    
    // Central flash for legendary/rare
    if (rarity === 'legendary' || rarity === 'rare') {
      const flash = this.add.circle(x, y, rarity === 'legendary' ? 60 : 40, 0xFFD700, 0.4).setDepth(999);
      this.tweens.add({
        targets: flash,
        scale: 2,
        alpha: 0,
        duration: 400,
        ease: 'Quad.easeOut',
        onComplete: () => flash.destroy()
      });
    }
  }
  
  // Update description when card is being dragged
  public updatePlayZoneDescription(text: string): void {
    if (this.playZoneDescText) {
      this.playZoneDescText.setText(text);
      this.playZoneDescText.setColor('#FFFFFF');
    }
  }
  
  public resetPlayZoneDescription(): void {
    if (this.playZoneDescText) {
      this.playZoneDescText.setText('Drag a card from your hand to play it');
      this.playZoneDescText.setColor('#CCCCCC');
    }
  }

  // ============ DAY/NIGHT CYCLE METHODS ============
  
  private updateTimeOfDay(): void {
    const cycleIndex = RESOURCES.year % 3;
    switch (cycleIndex) {
      case 0:
        this.currentTimeOfDay = 'morning';
        break;
      case 1:
        this.currentTimeOfDay = 'day';
        break;
      case 2:
        this.currentTimeOfDay = 'night';
        break;
    }
  }

  private getBackgroundKey(): string {
    switch (this.currentTimeOfDay) {
      case 'morning': return 'pryor_bg0';
      case 'day': return 'pryor_bg1';
      case 'night': return 'pryor_bg2';
      default: return 'pryor_bg1';
    }
  }

  // Simplified: only night_song plays as permanent background
  private startMusic(): void {
    this.audioManager.startBackgroundMusic();
  }

  private applyTimeOverlay(): void {
    // Reset overlays
    this.morningOverlay?.setAlpha(0);
    this.nightOverlay?.setAlpha(0);
    
    switch (this.currentTimeOfDay) {
      case 'morning':
        this.morningOverlay?.setAlpha(0.15);
        break;
      case 'night':
        this.nightOverlay?.setAlpha(0.25);
        break;
    }
  }

  private transitionTimeOfDay(): void {
    const oldTime = this.currentTimeOfDay;
    this.updateTimeOfDay();
    
    if (oldTime === this.currentTimeOfDay) return;
    
    console.log(`Day/Night transition: ${oldTime} → ${this.currentTimeOfDay}`);
    
    // Play year transition sound
    this.audioManager.playSFX('year');
    
    // Screen fade for smooth transition
    this.cameras.main.fadeOut(800, 0, 0, 0);
    
    this.cameras.main.once('camerafadeoutcomplete', () => {
      // Swap background
      const newBgKey = this.getBackgroundKey();
      this.backgroundImage.setTexture(newBgKey);
      
      // Scale background to fit
      const bgTexture = this.textures.get(newBgKey);
      if (bgTexture && bgTexture.source[0]) {
        const scaleX = GAME_WIDTH / bgTexture.source[0].width;
        const scaleY = GAME_HEIGHT / bgTexture.source[0].height;
        this.backgroundImage.setScale(Math.max(scaleX, scaleY));
      }
      
      // Apply time overlay
      this.applyTimeOverlay();
      
      // Update celestial body
      this.updateCelestialBody();
      
      // Update horse tints for new time of day
      this.horses.forEach(horse => horse.setTimeOfDay(this.currentTimeOfDay));
      
      // Fade back in
      this.cameras.main.fadeIn(1000, 0, 0, 0);
    });
    
    // Music is now a single looping track - no need to switch
  }

  // ============ AUDIO CONTROLS ============
  
  private createAudioControls(): void {
    // Position in top right corner with volume slider
    this.audioControlsContainer = this.add.container(GAME_WIDTH - 70, 20).setDepth(200);
    
    // Background panel
    const bg = this.add.rectangle(0, 0, 120, 30, COLORS.deepForest, 0.8)
      .setStrokeStyle(1, COLORS.bone);
    
    // Mute toggle button
    const muteBtn = this.add.text(-45, 0, this.audioManager.getMuted() ? '🔇' : '🔊', { 
      fontSize: '18px' 
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    muteBtn.on('pointerdown', () => {
      const muted = this.audioManager.toggleMute();
      muteBtn.setText(muted ? '🔇' : '🔊');
    });
    
    // Volume slider track
    const sliderTrack = this.add.rectangle(15, 0, 60, 6, 0x444444).setOrigin(0.5);
    
    // Volume slider fill
    const sliderFill = this.add.rectangle(-15, 0, 30, 6, COLORS.cornflower).setOrigin(0, 0.5);
    
    // Volume slider handle
    const sliderHandle = this.add.circle(15, 0, 8, COLORS.bone)
      .setInteractive({ useHandCursor: true, draggable: true });
    
    // Drag handling for volume
    sliderHandle.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number) => {
      const minX = -15;
      const maxX = 45;
      const clampedX = Phaser.Math.Clamp(dragX, minX, maxX);
      sliderHandle.x = clampedX;
      sliderFill.width = clampedX - minX;
      const volume = (clampedX - minX) / (maxX - minX);
      this.audioManager.setMusicVolume(volume);
      this.audioManager.setSFXVolume(volume);
    });
    
    this.input.setDraggable(sliderHandle);
    
    this.audioControlsContainer.add([bg, muteBtn, sliderTrack, sliderFill, sliderHandle]);
  }

  // ============ JOURNAL ============

  private createJournal(): void {
    // Journal positioned on right side - moved down to not overlap play zone
    this.journalContainer = this.add.container(GAME_WIDTH - 90, 180);
    
    const bg = this.add.rectangle(0, 0, 160, 120, COLORS.deepForest, 0.9)
      .setStrokeStyle(1, COLORS.bone);
    
    const title = this.add.text(0, -50, '📜 Journal', {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '12px',
      color: '#f4e4bc',
      stroke: '#000000',
      strokeThickness: 1
    }).setOrigin(0.5);
    
    this.journalContainer.add([bg, title]);
    this.journalContainer.setDepth(60);
    
    // Add initial entry
    this.addJournalEntry('Journey begins!');
  }

  private addJournalEntry(text: string): void {
    GAME_STATE.journalEntries.push(`${RESOURCES.year}: ${text}`);
    
    // Keep only last 4 entries displayed
    const displayEntries = GAME_STATE.journalEntries.slice(-4);
    
    // Clear old entries from container (keep bg and title)
    while (this.journalContainer.list.length > 2) {
      const child = this.journalContainer.list[2];
      if (child) {
        child.destroy();
      }
    }
    
    // Add new entries - wider text area
    displayEntries.forEach((entry, i) => {
      const entryText = this.add.text(-72, -32 + i * 20, entry.substring(0, 24), {
        fontFamily: 'Source Sans 3, sans-serif',
        fontSize: '10px',
        color: '#d4c4a4',
        stroke: '#000000',
        strokeThickness: 0.5
      });
      this.journalContainer.add(entryText);
    });
  }

  private updateSeasonDisplay(): void {
    // Season based on year quarter
    const yearProgress = (RESOURCES.year - 2025) % 1;
    const seasons: Array<'spring' | 'summer' | 'autumn' | 'winter'> = ['spring', 'summer', 'autumn', 'winter'];
    const seasonIndex = Math.floor(((RESOURCES.year - 2025) * 4) % 4);
    this.season = seasons[seasonIndex] || 'spring';
  }

  private initializeDeck(): void {
    // Deck Evolution: Use persistent deck if exists, otherwise create starter
    if (GAME_STATE.persistentDeck.length > 0) {
      this.deck = [...GAME_STATE.persistentDeck];
    } else {
      // Create initial deck from starter cards
      this.deck = [];
      pryorStarterDeck.forEach(card => {
        // Fewer copies of negative events (balanced 60% positive)
        const copies = card.type === 'event' && 
          (card.id.includes('drought') || card.id.includes('fire') || card.id.includes('harsh') || card.id.includes('budget')) 
          ? 1 : 2;
        for (let i = 0; i < copies; i++) {
          this.deck.push({ ...card });
        }
      });
      GAME_STATE.persistentDeck = [...this.deck];
    }
    this.shuffleDeck();
  }

  private shuffleDeck(): void {
    for (let i = this.deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.deck[i], this.deck[j]] = [this.deck[j], this.deck[i]];
    }
  }

  private spawnHorses(): void {
    // Clear existing horses
    this.horses.forEach(h => h.destroy());
    this.horses = [];
    
    // Clear existing burros
    this.burros.forEach(b => b.destroy());
    this.burros = [];
    
    // Spawn horses (visual representation, min 5, max 12 shown)
    const visualCount = Math.min(12, Math.max(5, Math.floor(RESOURCES.population / 5)));
    
    for (let i = 0; i < visualCount; i++) {
      // Spawn ON THE GRASS - keep away from bottom UI (y: 380-520)
      const x = Phaser.Math.Between(100, 800);
      const y = Phaser.Math.Between(380, 520);
      const horse = new HorseSprite(this, x, y);
      horse.setTimeOfDay(this.currentTimeOfDay);
      horse.setDepth(30); // Below cards (800+) and action bar (100)
      this.horses.push(horse);
    }
    
    // Spawn burros (17% of visual count, at least 1)
    const burroCount = Math.max(1, Math.floor(visualCount * 0.17));
    for (let i = 0; i < burroCount; i++) {
      this.spawnBurro();
    }
    
    // Start random horse behaviors
    this.startHorseBehaviors();
  }
  
  // Remove some visual horses (for adoption animations)
  private removeVisualHorses(count: number): void {
    const toRemove = Math.min(count, this.horses.length - 2); // Keep at least 2
    for (let i = 0; i < toRemove; i++) {
      const horse = this.horses.pop();
      if (horse) {
        // Animate horse running off screen
        this.tweens.add({
          targets: horse,
          x: -100,
          alpha: 0,
          duration: 1500,
          ease: 'Power2',
          onComplete: () => horse.destroy()
        });
      }
    }
  }
  
  // Add visual horses (for foal births)
  private addVisualHorses(count: number): void {
    const maxVisual = 8;
    const toAdd = Math.min(count, maxVisual - this.horses.length);
    for (let i = 0; i < toAdd; i++) {
      const x = Phaser.Math.Between(100, 800);
      const y = Phaser.Math.Between(380, 520);
      const horse = new HorseSprite(this, x, y);
      horse.setTimeOfDay(this.currentTimeOfDay);
      horse.setDepth(30);
      horse.setScale(0);
      this.horses.push(horse);
      
      // Pop-in animation
      this.tweens.add({
        targets: horse,
        scaleX: 0.5,
        scaleY: 0.5,
        duration: 500,
        ease: 'Back.easeOut'
      });
    }
  }
  
  // Random horse behaviors - stallion fights and foal following
  private startHorseBehaviors(): void {
    // Random stallion fight every 20-40 seconds
    this.time.addEvent({
      delay: Phaser.Math.Between(20000, 40000),
      loop: true,
      callback: () => {
        if (this.horses.length >= 2 && Math.random() < 0.3) {
          const horse1 = Phaser.Math.RND.pick(this.horses);
          const horse2 = Phaser.Math.RND.pick(this.horses.filter(h => h !== horse1));
          if (horse1 && horse2) {
            horse1.stallionFight(horse2);
          }
        }
      }
    });
    
    // Random foal following every 15-30 seconds
    this.time.addEvent({
      delay: Phaser.Math.Between(15000, 30000),
      loop: true,
      callback: () => {
        if (this.horses.length >= 2 && Math.random() < 0.4) {
          const mom = Phaser.Math.RND.pick(this.horses);
          const foal = Phaser.Math.RND.pick(this.horses.filter(h => h !== mom));
          if (mom && foal) {
            foal.followMom(mom);
          }
        }
      }
    });
  }
  
  // Spawn a horse matching the card played (e.g., "Grulla Filly" spawns grulla horse)
  private spawnHorseFromCard(card: CardData): void {
    // Only spawn for horse-type cards
    if (card.type !== 'horse') return;
    
    // Use card's spawnColor if defined, otherwise fallback to title parsing
    let variantName: string | undefined = card.spawnColor;
    
    if (!variantName) {
      const title = card.title.toLowerCase();
      // Fallback: Check specific color keywords in priority order
      if (title.includes('paint') || title.includes('pinto')) {
        variantName = 'paint';
      } else if (title.includes('black')) {
        variantName = 'black';
      } else if (title.includes('grulla')) {
        variantName = 'grulla';
      } else if (title.includes('buckskin')) {
        variantName = 'buckskin';
      } else if (title.includes('bay')) {
        variantName = 'bay';
      } else if (title.includes('dun') && !title.includes('fund')) {
        variantName = 'bay'; // dun uses bay tint
      } else if (title.includes('roan')) {
        variantName = 'roan';
      } else if (title.includes('sorrel')) {
        variantName = 'sorrel';
      }
    }
    
    // Only spawn ONE horse per card, and only if we have room
    if (this.horses.length < 10) {
      const x = Phaser.Math.Between(200, 700);
      const y = Phaser.Math.Between(490, 570);
      
      // Create horse with explicit variant
      const horse = new HorseSprite(this, x, y, variantName);
      horse.setTimeOfDay(this.currentTimeOfDay);
      
      // Pop-in animation
      horse.setScale(0);
      this.tweens.add({
        targets: horse,
        scaleX: 1,
        scaleY: 1,
        duration: 500,
        ease: 'Back.easeOut'
      });
      
      // Horse reacts happily
      this.time.delayedCall(300, () => horse.react('happy'));
      
      // Add to herd array
      this.horses.push(horse);
    }
  }
  
  // Spawn a burro when the burro BFF event occurs
  private spawnBurro(): void {
    const x = Phaser.Math.Between(150, 750);
    const y = Phaser.Math.Between(420, 520);
    
    // Create burro using the burro sprite asset
    const burro = this.add.image(x, y, 'burro')
      .setScale(0.12)
      .setOrigin(0.5)
      .setDepth(35);
    
    // Pop-in animation
    burro.setScale(0);
    this.tweens.add({
      targets: burro,
      scale: 0.12,
      duration: 500,
      ease: 'Back.easeOut'
    });
    
    // Add heart emoji above burro
    const heart = this.add.text(x, y - 40, '❤️', { fontSize: '24px' }).setOrigin(0.5);
    this.tweens.add({
      targets: heart,
      y: heart.y - 30,
      alpha: 0,
      duration: 1500,
      onComplete: () => heart.destroy()
    });
    
    // Track burro
    this.burros.push(burro);
    
    // Make burro wander slowly
    this.time.addEvent({
      delay: 3000,
      loop: true,
      callback: () => {
        if (!burro.active) return;
        const newX = Phaser.Math.Clamp(burro.x + Phaser.Math.Between(-50, 50), 100, 800);
        const newY = Phaser.Math.Clamp(burro.y + Phaser.Math.Between(-20, 20), 420, 500);
        this.tweens.add({
          targets: burro,
          x: newX,
          y: newY,
          duration: 2000,
          ease: 'Sine.easeInOut'
        });
      }
    });
  }

  private updateHorseCount(): void {
    // Always show at least 5 horses, up to 12 based on population
    const targetCount = Math.min(12, Math.max(5, Math.floor(RESOURCES.population / 5)));
    
    while (this.horses.length < targetCount) {
      // Spawn ON THE GRASS
      const x = Phaser.Math.Between(80, 820);
      const y = Phaser.Math.Between(480, 620);
      const horse = new HorseSprite(this, x, y);
      horse.setTimeOfDay(this.currentTimeOfDay);
      this.horses.push(horse);
      this.makeHorseInteractive(horse); // Ensure new horses are interactive
    }
    
    while (this.horses.length > targetCount) {
      const horse = this.horses.pop();
      if (horse) {
        this.tweens.add({
          targets: horse,
          alpha: 0,
          duration: 500,
          onComplete: () => horse.destroy()
        });
      }
    }
  }

  private drawHand(): void {
    // Clear current hand visuals only (cards stay in hand for carryover)
    this.hand.forEach(card => card.destroy());
    this.hand = [];
    
    // Deck Evolution: Reshuffle discard into deck if needed
    if (this.deck.length < 5) {
      // Add discard pile back to deck
      this.deck = [...this.deck, ...GAME_STATE.discardPile];
      GAME_STATE.discardPile = [];
      this.shuffleDeck();
      
      // Chance for bonus card drops!
      this.checkForCardDrops();
    }
    
    // Draw 5 cards
    const cardsToDraw = Math.min(5, this.deck.length);
    
    for (let i = 0; i < cardsToDraw; i++) {
      const cardData = this.deck.pop();
      if (cardData) {
        const card = new Card(this, 0, 0, cardData, i);
        this.hand.push(card);
        this.handContainer.add(card);
      }
    }
    
    this.rearrangeHand();
  }

  private drawSingleCard(): void {
    // Reshuffle if needed
    if (this.deck.length === 0) {
      this.deck = [...GAME_STATE.discardPile];
      GAME_STATE.discardPile = [];
      this.shuffleDeck();
    }
    
    const cardData = this.deck.pop();
    if (cardData) {
      const card = new Card(this, 0, 0, cardData, this.hand.length);
      this.hand.push(card);
      this.handContainer.add(card);
    }
  }

  private checkForCardDrops(): void {
    // 50% chance for +1 card, 20% chance for +2 cards
    const roll = Math.random();
    let drops = 0;
    
    if (roll < 0.2) {
      drops = 2;
    } else if (roll < 0.5) {
      drops = 1;
    }
    
    for (let i = 0; i < drops; i++) {
      const newCard = getRandomCardDrop();
      if (newCard) {
        GAME_STATE.persistentDeck.push({ ...newCard });
        this.deck.push({ ...newCard });
        
        // Only show event if eventText exists (not during initial load)
        if (this.eventText) {
          this.showEvent(`✨ New card: ${newCard.title}!`);
          this.addJournalEntry(`Found: ${newCard.title}`);
          
          // Special announcement for legendaries
          if (newCard.rarity === 'legendary') {
            this.showLegendaryDrop(newCard);
          }
        }
      }
    }
  }

  private showLegendaryDrop(card: CardData): void {
    // Epic legendary card announcement!
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.8);
    
    const container = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    
    const glow = this.add.rectangle(0, 0, 300, 400, 0xffd700, 0.3);
    const title = this.add.text(0, -150, '⭐ LEGENDARY! ⭐', {
      fontFamily: 'Georgia',
      fontSize: '32px',
      color: '#ffd700'
    }).setOrigin(0.5);
    
    const cardArt = this.add.text(0, -50, card.art, {
      fontSize: '80px'
    }).setOrigin(0.5);
    
    const cardName = this.add.text(0, 30, card.title, {
      fontFamily: 'Georgia',
      fontSize: '24px',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    const bio = this.add.text(0, 80, card.bio || card.description, {
      fontFamily: 'Georgia',
      fontSize: '14px',
      color: '#d4c4a4',
      wordWrap: { width: 280 },
      align: 'center'
    }).setOrigin(0.5);
    
    const dismiss = this.add.text(0, 160, '[ Click to continue ]', {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#88ff88'
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    
    container.add([glow, title, cardArt, cardName, bio, dismiss]);
    
    // Pulse animation
    this.tweens.add({
      targets: glow,
      scaleX: 1.1,
      scaleY: 1.1,
      alpha: 0.5,
      duration: 500,
      yoyo: true,
      repeat: -1
    });
    
    dismiss.on('pointerdown', () => {
      overlay.destroy();
      container.destroy();
    });
  }

  private rearrangeHand(): void {
    // Filter out destroyed cards from the hand array
    this.hand = this.hand.filter(card => card.active);
    
    // Cards at very bottom - compact spacing for small cards
    const cardSpacing = 110; // Tight spacing for 100px wide cards
    const startX = GAME_WIDTH / 2 - (this.hand.length - 1) * (cardSpacing / 2);
    const baseY = GAME_HEIGHT - 75; // Very bottom of screen
    
    this.hand.forEach((card, index) => {
      const targetX = startX + index * cardSpacing;
      const rotation = (index - (this.hand.length - 1) / 2) * 0.02; // Minimal rotation
      const yOffset = Math.abs(index - (this.hand.length - 1) / 2) * 3;
      
      // Set high depth so cards are ABOVE horses
      card.setDepth(800 + index);
      
      this.tweens.add({
        targets: card,
        x: targetX,
        y: baseY + yOffset,
        rotation: rotation,
        duration: 300,
        ease: 'Back.easeOut'
      });
      
      card.setHomePosition(targetX, baseY + yOffset);
    });
    
    // Update buy cards button visibility after animations complete
    this.time.delayedCall(400, () => {
      this.updateBuyCardsButton();
    });
  }

  // ============ ACTION POINTS & INTERACTIVE ELEMENTS ============
  
  private createActionPointsDisplay(): void {
    // Action points indicator below end year button area
    const container = this.add.container(GAME_WIDTH - 90, 330);
    
    const bg = this.add.rectangle(0, 0, 100, 30, COLORS.deepForest, 0.8)
      .setStrokeStyle(1, COLORS.bone);
    
    const label = this.add.text(0, -5, '⚡ ACTIONS', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '10px',
      color: '#B0B0A0'
    }).setOrigin(0.5);
    
    this.actionPointsText = this.add.text(0, 8, `${ACTION_SYSTEM.actionsRemaining}/${ACTION_SYSTEM.actionsPerYear}`, {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#FFD700'
    }).setOrigin(0.5);
    
    container.add([bg, label, this.actionPointsText]);
    container.setDepth(60);
  }
  
  private createYearCounter(): void {
    // Year counter at bottom left with emoji
    const yearsPlayed = RESOURCES.year - 1990;
    const yearsRemaining = END_YEAR - RESOURCES.year;
    
    this.yearCounterText = this.add.text(20, GAME_HEIGHT - 20, 
      `📅 Year ${RESOURCES.year} (${yearsPlayed}/20)`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '12px',
      color: '#D2CFBF',
      backgroundColor: '#163F29',
      padding: { x: 6, y: 3 }
    }).setOrigin(0, 1).setDepth(100);
  }
  
  private updateYearCounter(): void {
    if (this.yearCounterText) {
      const yearsPlayed = RESOURCES.year - 1990;
      this.yearCounterText.setText(`📅 Year ${RESOURCES.year} (${yearsPlayed}/20)`);
    }
  }
  
  private createInteractiveElements(): void {
    // Make horses clickable for interaction
    this.horses.forEach(horse => this.makeHorseInteractive(horse));
    
    // Salt block button (bottom left)
    this.createSaltBlockButton();
    
    // Breeding button (bottom left, above salt)
    this.createBreedingButton();
    
    // NEW ACTION BUTTONS (right side)
    this.createPartyButton();
    this.createWaterTroughButton();
    this.createScoutButton();
    this.createAdoptButton();
    
    // Buy cards button (shown when hand is empty)
    this.createBuyCardsButton();
    
    // Action progress bar (hidden by default)
    this.createActionProgressBar();
  }
  
  private makeHorseInteractive(horse: HorseSprite): void {
    // Containers need explicit hit area - use a LARGER rectangle for easier clicking
    horse.setSize(100, 80);
    horse.setInteractive(
      new Phaser.Geom.Rectangle(-50, -40, 100, 80),
      Phaser.Geom.Rectangle.Contains
    );
    horse.input!.cursor = 'pointer';
    
    horse.on('pointerdown', () => {
      this.petHorse(horse);
    });
    
    // Store hover text reference on horse
    let hoverText: Phaser.GameObjects.Text | null = null;
    
    horse.on('pointerover', () => {
      // Subtle highlight - respect foal scale
      const baseScale = horse.getAge(RESOURCES.year) < 2 ? 0.6 : 1;
      this.tweens.add({
        targets: horse,
        scaleX: baseScale * 1.1,
        scaleY: baseScale * 1.1,
        duration: 150
      });
      
      // Show age/gender info
      const info = horse.getInfoString(RESOURCES.year);
      hoverText = this.add.text(horse.x, horse.y - 50, info, {
        fontFamily: 'Source Sans 3, sans-serif',
        fontSize: '12px',
        color: '#FFFFFF',
        backgroundColor: '#333333',
        padding: { x: 4, y: 2 }
      }).setOrigin(0.5).setDepth(1000);
    });
    
    horse.on('pointerout', () => {
      // Restore scale - respect foal scale
      const baseScale = horse.getAge(RESOURCES.year) < 2 ? 0.6 : 1;
      this.tweens.add({
        targets: horse,
        scaleX: baseScale,
        scaleY: baseScale,
        duration: 150
      });
      
      // Remove hover text
      if (hoverText) {
        hoverText.destroy();
        hoverText = null;
      }
    });
  }
  
  // Pet the horse - show hearts, play whinny, add health, track clicks for halter breaking
  private petHorse(horse: HorseSprite): void {
    // If already in halter breaking process, ignore clicks
    if (this.halterProgressBar) {
      return;
    }
    
    // If we're in halter breaking mode for this horse, start the progress
    if (this.halterBreakingHorse === horse) {
      this.startHalterBreaking(horse);
      return;
    }
    
    // If another horse has halter mode active, ignore this click
    if (this.halterBreakingHorse && this.halterBreakingHorse !== horse) {
      return;
    }
    
    // Track clicks for halter breaking - only count clicks on the SAME horse
    const currentClicks = (this.horseClickCounts.get(horse) || 0) + 1;
    this.horseClickCounts.set(horse, currentClicks);
    
    // After 5 clicks on same horse, show halter and enable halter breaking mode
    if (currentClicks >= 5 && !this.halterBreakingHorse) {
      this.showHalterOnHorse(horse);
      return;
    }
    
    // Show 3-5 floating heart emojis
    const heartCount = Phaser.Math.Between(3, 5);
    for (let i = 0; i < heartCount; i++) {
      const heart = this.add.text(
        horse.x + Phaser.Math.Between(-20, 20), 
        horse.y - 30 + Phaser.Math.Between(-10, 10), 
        '❤️', 
        { fontSize: '24px' }
      ).setOrigin(0.5).setDepth(1000);
      
      this.tweens.add({
        targets: heart,
        y: heart.y - 60 - (i * 15),
        x: heart.x + Phaser.Math.Between(-30, 30),
        alpha: 0,
        scale: 1.3,
        duration: 1200,
        delay: i * 100,
        ease: 'Sine.easeOut',
        onComplete: () => heart.destroy()
      });
    }
    
    // Horse jumps happily
    this.tweens.add({
      targets: horse,
      y: horse.y - 15,
      duration: 150,
      yoyo: true,
      ease: 'Bounce.easeOut'
    });
    
    // Play soft whinny SFX (sparingly - 30% chance)
    if (Math.random() < 0.3) {
      this.audioManager.playSFX('horse');
    }
    
    // Add +1 to herd health
    RESOURCES.herdHealth = Math.min(100, RESOURCES.herdHealth + 1);
    this.resourceDisplay.update();
    
    // Small visual feedback for health increase
    const healthUp = this.add.text(horse.x, horse.y - 60, '+1 ❤️', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      color: '#88ff88'
    }).setOrigin(0.5).setDepth(1001);
    
    this.tweens.add({
      targets: healthUp,
      y: healthUp.y - 30,
      alpha: 0,
      duration: 1000,
      onComplete: () => healthUp.destroy()
    });
  }
  
  // Show red halter on horse after 3 clicks
  private showHalterOnHorse(horse: HorseSprite): void {
    this.halterBreakingHorse = horse;
    
    // Create red halter overlay (simple red lines/straps)
    const halter = this.add.text(horse.x, horse.y - 10, '🔴', {
      fontSize: '12px'
    }).setOrigin(0.5).setDepth(1001);
    (horse as any).halterSprite = halter;
    
    // Show instruction
    this.showAlertBox('🐴', 'Click to start halter breaking!', 3000);
    
    // Flash effect
    this.tweens.add({
      targets: halter,
      alpha: 0.5,
      duration: 500,
      yoyo: true,
      repeat: -1
    });
  }
  
  // Start the halter breaking progress bar
  private startHalterBreaking(horse: HorseSprite): void {
    // Prevent multiple progress bars
    if (this.halterProgressBar) return;
    
    // Remove halter flash
    if ((horse as any).halterSprite) {
      this.tweens.killTweensOf((horse as any).halterSprite);
      (horse as any).halterSprite.setAlpha(1);
    }
    
    // Pause horse movement
    horse.pauseMovement();
    
    // Create progress bar above horse - vertical bar that fills upward
    this.halterProgressBar = this.add.container(horse.x, horse.y - 70);
    this.halterProgressBar.setDepth(2000);
    
    // Background bar (dark)
    const barBg = this.add.rectangle(0, 25, 10, 50, 0x222222).setOrigin(0.5, 0.5);
    barBg.setStrokeStyle(2, 0xFFFFFF);
    
    // Fill bar starts at bottom, grows upward
    const barFill = this.add.rectangle(0, 50, 6, 0, 0x00FF00).setOrigin(0.5, 1);
    
    this.halterProgressBar.add([barBg, barFill]);
    
    // Horse follows mouse during halter breaking
    const followMouse = (pointer: Phaser.Input.Pointer) => {
      if (this.halterBreakingHorse === horse && this.halterProgressBar) {
        // Move horse toward mouse slowly
        const dx = pointer.x - horse.x;
        const dy = pointer.y - horse.y;
        horse.x += dx * 0.03;
        horse.y = Phaser.Math.Clamp(horse.y + dy * 0.03, 400, 520);
        
        // Flip horse to face direction of movement
        if (dx > 5) {
          horse.setFlipX(false); // Face right
        } else if (dx < -5) {
          horse.setFlipX(true); // Face left
        }
        
        // Update progress bar position
        this.halterProgressBar.setPosition(horse.x, horse.y - 70);
        
        // Update halter position
        if ((horse as any).halterSprite) {
          (horse as any).halterSprite.setPosition(horse.x, horse.y - 10);
        }
      }
    };
    
    this.input.on('pointermove', followMouse);
    
    // Animate progress bar filling over 5 seconds (grows upward)
    this.tweens.add({
      targets: barFill,
      height: 46,
      y: 4, // Move origin up as it grows
      duration: 5000,
      ease: 'Linear',
      onComplete: () => {
        // Remove mouse follow
        this.input.off('pointermove', followMouse);
        
        // Halter breaking complete!
        this.completeHalterBreaking(horse);
      }
    });
  }
  
  // Complete halter breaking - show popup with adopt option
  private completeHalterBreaking(horse: HorseSprite): void {
    // Clean up
    if (this.halterProgressBar) {
      this.halterProgressBar.destroy();
      this.halterProgressBar = undefined;
    }
    
    // Resume horse movement
    horse.resumeMovement();
    
    // Show completion popup
    const modal = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2);
    modal.setDepth(3000);
    
    const bg = this.add.rectangle(0, 0, 350, 200, 0x1A2F1A, 0.98)
      .setStrokeStyle(3, 0xD2CFBF);
    
    const title = this.add.text(0, -70, '🎉 Halter Broke!', {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '24px',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    const horseName = horse.getDisplayName(RESOURCES.year);
    const desc = this.add.text(0, -30, `This ${horseName} is now halter broke!\nReady for a premium adoption.`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      color: '#FFFFFF',
      align: 'center'
    }).setOrigin(0.5);
    
    const adoptPrice = 2000; // Double the normal adoption price
    
    // Adopt button
    const adoptBtn = this.add.container(0, 30);
    const adoptBg = this.add.rectangle(0, 0, 200, 40, 0x4A7C59)
      .setStrokeStyle(2, 0x88CC88)
      .setInteractive({ useHandCursor: true });
    const adoptText = this.add.text(0, 0, `Adopt for $${adoptPrice}`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '16px',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 1
    }).setOrigin(0.5);
    adoptBtn.add([adoptBg, adoptText]);
    
    // Close button
    const closeBtn = this.add.container(0, 75);
    const closeBg = this.add.rectangle(0, 0, 100, 30, 0x666666)
      .setStrokeStyle(1, 0x888888)
      .setInteractive({ useHandCursor: true });
    const closeText = this.add.text(0, 0, 'Keep', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      color: '#CCCCCC'
    }).setOrigin(0.5);
    closeBtn.add([closeBg, closeText]);
    
    modal.add([bg, title, desc, adoptBtn, closeBtn]);
    
    // Adopt button action
    adoptBg.on('pointerover', () => adoptBg.setFillStyle(0x5A8C69));
    adoptBg.on('pointerout', () => adoptBg.setFillStyle(0x4A7C59));
    adoptBg.on('pointerdown', () => {
      // Remove horse from herd
      RESOURCES.population = Math.max(1, RESOURCES.population - 1);
      RESOURCES.funds += adoptPrice;
      RESOURCES.reputation = Math.min(100, RESOURCES.reputation + 8);
      
      // Remove visual horse
      const idx = this.horses.indexOf(horse);
      if (idx > -1) this.horses.splice(idx, 1);
      
      // Clean up halter
      if ((horse as any).halterSprite) {
        (horse as any).halterSprite.destroy();
      }
      horse.destroy();
      
      this.resourceDisplay.update();
      this.showAlertBox('🚚', `${horseName} adopted for $${adoptPrice}!`, 3000);
      this.addJournalEntry(`Halter broke ${horseName} adopted`);
      
      // Reset halter breaking state
      this.halterBreakingHorse = undefined;
      this.horseClickCounts.delete(horse);
      
      modal.destroy();
    });
    
    // Close button action
    closeBg.on('pointerover', () => closeBg.setFillStyle(0x777777));
    closeBg.on('pointerout', () => closeBg.setFillStyle(0x666666));
    closeBg.on('pointerdown', () => {
      // Keep the horse but mark as halter broke
      (horse as any).isHalterBroke = true;
      
      // Reset halter breaking state
      this.halterBreakingHorse = undefined;
      this.horseClickCounts.delete(horse);
      
      // Remove halter sprite
      if ((horse as any).halterSprite) {
        (horse as any).halterSprite.destroy();
      }
      
      modal.destroy();
    });
  }
  
  private createSaltBlockButton(): void {
    // Position: far left in horizontal row above cards
    const btn = this.add.container(GAME_WIDTH / 2 + 0, GAME_HEIGHT - 25).setDepth(100);
    
    const bg = this.add.rectangle(0, 0, 50, 40, COLORS.bone, 0.95)
      .setStrokeStyle(2, COLORS.oliveWood);
    const icon = this.add.text(0, -6, '🧂', { fontSize: '16px' }).setOrigin(0.5);
    const label = this.add.text(0, 12, 'SALT', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '8px',
      color: '#163F29'
    }).setOrigin(0.5);
    
    btn.add([bg, icon, label]);
    btn.setSize(50, 40);
    btn.setInteractive({ useHandCursor: true });
    
    btn.on('pointerover', () => bg.setFillStyle(0xE8E4D8));
    btn.on('pointerout', () => bg.setFillStyle(COLORS.bone));
    
    btn.on('pointerdown', () => {
      if (ACTION_SYSTEM.actionsRemaining <= 0) {
        this.showEvent('⚠️ No actions left this year!');
        return;
      }
      if (RESOURCES.funds < 5000) {
        this.showEvent('⚠️ Need $5000 for salt block!');
        return;
      }
      this.placeSaltBlock();
    });
  }
  
  private placeSaltBlock(): void {
    // Costs money and an action - $5k per action
    ACTION_SYSTEM.actionsRemaining--;
    RESOURCES.funds -= 5000;
    this.updateActionPoints();
    
    // Remove old salt block if exists
    if (this.saltBlock) {
      this.saltBlock.destroy();
    }
    
    // Place salt block in grass area
    const x = Phaser.Math.Between(200, 700);
    const y = Phaser.Math.Between(500, 580);
    
    this.saltBlock = this.add.container(x, y).setDepth(50);
    
    const block = this.add.rectangle(0, 0, 30, 20, 0xE8E0D0)
      .setStrokeStyle(1, 0x888888);
    const emoji = this.add.text(0, -20, '🧂', { fontSize: '20px' }).setOrigin(0.5);
    
    this.saltBlock.add([block, emoji]);
    
    // Attract horses to salt block
    this.attractHorsesToSalt(x, y);
    
    this.addJournalEntry('Placed salt block 🧂');
    this.showEvent('🧂 Salt block placed!');
    
    // Salt block benefits
    RESOURCES.herdHealth = Math.min(100, RESOURCES.herdHealth + 5);
    this.resourceDisplay.update();
  }
  
  private attractHorsesToSalt(saltX: number, saltY: number): void {
    // Horses gradually move toward salt and show happy emojis
    this.horses.forEach((horse, i) => {
      this.time.delayedCall(i * 500, () => {
        if (!horse.active) return;
        
        // Move toward salt (with some randomness)
        const targetX = saltX + Phaser.Math.Between(-50, 50);
        const targetY = saltY + Phaser.Math.Between(-30, 30);
        
        this.tweens.add({
          targets: horse,
          x: Phaser.Math.Clamp(targetX, 100, 800),
          y: Phaser.Math.Clamp(targetY, 480, 620),
          duration: 2000 + i * 300,
          ease: 'Sine.easeInOut',
          onComplete: () => {
            // Show happy emoji when reaching salt
            if (horse.active) {
              const happyEmoji = this.add.text(horse.x, horse.y - 30, 
                Phaser.Math.RND.pick(['😋', '🤤', '😊', '❤️', '✨']), 
                { fontSize: '20px' }
              ).setOrigin(0.5).setDepth(1000);
              
              this.tweens.add({
                targets: happyEmoji,
                y: happyEmoji.y - 30,
                alpha: 0,
                duration: 1200,
                onComplete: () => happyEmoji.destroy()
              });
            }
          }
        });
      });
    });
    
    // Attract more horses from the existing population pool to the screen
    // Only add visual horses if population > current visual count
    const currentVisual = this.horses.length;
    const maxVisual = Math.min(RESOURCES.population, 25); // Cap at 25 visual horses
    const horsesToAdd = Math.min(3, maxVisual - currentVisual); // Add up to 3 more
    
    if (horsesToAdd > 0) {
      this.showAlertBox('🧂', `${horsesToAdd} more horses attracted!`, 2500);
      
      for (let i = 0; i < horsesToAdd; i++) {
        this.time.delayedCall(1000 + i * 800, () => {
          // Spawn from off-screen and walk toward salt
          const startX = Phaser.Math.RND.pick([-50, GAME_WIDTH + 50]);
          const startY = Phaser.Math.Between(450, 550);
          
          const newHorse = new HorseSprite(
            this,
            startX,
            startY,
            RESOURCES.year,
            Phaser.Math.RND.pick(['mare', 'stallion']) as 'mare' | 'stallion'
          );
          
          this.horses.push(newHorse);
          this.makeHorseInteractive(newHorse);
          
          // Walk toward salt block
          const targetX = saltX + Phaser.Math.Between(-60, 60);
          const targetY = saltY + Phaser.Math.Between(-40, 40);
          
          this.tweens.add({
            targets: newHorse,
            x: Phaser.Math.Clamp(targetX, 100, 800),
            y: Phaser.Math.Clamp(targetY, 450, 580),
            duration: 3000,
            ease: 'Sine.easeInOut',
            onComplete: () => {
              // Show happy emoji
              const happyEmoji = this.add.text(newHorse.x, newHorse.y - 30, '😋', { fontSize: '20px' })
                .setOrigin(0.5).setDepth(1000);
              this.tweens.add({
                targets: happyEmoji,
                y: happyEmoji.y - 30,
                alpha: 0,
                duration: 1200,
                onComplete: () => happyEmoji.destroy()
              });
            }
          });
        });
      }
    }
  }
  
  private createBreedingButton(): void {
    // Position: far right in horizontal row above cards
    const btn = this.add.container(GAME_WIDTH / 2 + 60, GAME_HEIGHT - 25).setDepth(100);
    
    const bg = this.add.rectangle(0, 0, 50, 40, COLORS.bone, 0.95)
      .setStrokeStyle(2, COLORS.oliveWood);
    const icon = this.add.text(0, -6, '💕', { fontSize: '16px' }).setOrigin(0.5);
    const label = this.add.text(0, 12, 'BREED', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '8px',
      color: '#163F29'
    }).setOrigin(0.5);
    
    btn.add([bg, icon, label]);
    btn.setSize(50, 40);
    btn.setInteractive({ useHandCursor: true });
    
    btn.on('pointerover', () => bg.setFillStyle(0xE8E4D8));
    btn.on('pointerout', () => bg.setFillStyle(COLORS.bone));
    
    btn.on('pointerdown', () => {
      if (ACTION_SYSTEM.actionsRemaining < 1) {
        this.showEvent('⚠️ No actions remaining this year!');
        return;
      }
      if (RESOURCES.funds < 5000) {
        this.showEvent('⚠️ Need $5000 for breeding program!');
        return;
      }
      if (RESOURCES.population < 5) {
        this.showEvent('⚠️ Need at least 5 horses to breed');
        return;
      }
      this.initiateBreeding();
    });
  }
  
  private initiateBreeding(): void {
    // Breeding costs 1 action and $5k
    ACTION_SYSTEM.actionsRemaining--;
    RESOURCES.funds -= 5000;
    this.updateActionPoints();
    this.resourceDisplay.update();
    
    // Foals based on mares (roughly half the herd) and diversity affects stallion contribution
    const estimatedMares = Math.floor(RESOURCES.population / 2);
    const diversityBonus = RESOURCES.diversity / 100;
    const basefoals = Math.max(2, Math.min(5, Math.floor(estimatedMares * 0.3 * (1 + diversityBonus))));
    const foalsToAdd = Phaser.Math.Between(2, basefoals);
    
    if (foalsToAdd > 0) {
      RESOURCES.population += foalsToAdd;
      GAME_STATE.totalFoalsBorn += foalsToAdd;
      
      // Diversity boost from controlled breeding
      RESOURCES.diversity = Math.min(100, RESOURCES.diversity + 3);
      
      // Show breeding animation
      this.showBreedingAnimation(foalsToAdd);
      
      this.showEvent(`💕 ${foalsToAdd} foal${foalsToAdd > 1 ? 's' : ''} born!`);
      this.addJournalEntry(`Breeding: +${foalsToAdd} foals`);
      
      // Spawn visual foals with proper birth year and small scale
      for (let i = 0; i < Math.min(foalsToAdd, 2); i++) {
        this.time.delayedCall(i * 500, () => {
          if (this.horses.length < 12) {
            const x = Phaser.Math.Between(200, 700);
            const y = Phaser.Math.Between(420, 500);
            // Create foal with current year as birth year
            const gender = Math.random() < 0.5 ? 'female' : 'male';
            const horse = new HorseSprite(this, x, y, undefined, false, RESOURCES.year, gender);
            horse.setTimeOfDay(this.currentTimeOfDay);
            horse.setScale(0);
            this.tweens.add({
              targets: horse,
              scaleX: 0.6, // Foals are smaller (under 2 years)
              scaleY: 0.6,
              duration: 500,
              ease: 'Back.easeOut'
            });
            this.makeHorseInteractive(horse);
            this.horses.push(horse);
          }
        });
      }
    } else {
      this.showEvent('💔 No foals this season...');
      this.addJournalEntry('Breeding unsuccessful');
    }
    
    this.resourceDisplay.update();
  }
  
  private showBreedingAnimation(foalCount: number): void {
    // Heart particles burst
    for (let i = 0; i < foalCount * 3; i++) {
      const heart = this.add.text(
        GAME_WIDTH / 2 + Phaser.Math.Between(-100, 100),
        500 + Phaser.Math.Between(-50, 50),
        Phaser.Math.RND.pick(['💕', '❤️', '💖', '🐴']),
        { fontSize: '24px' }
      ).setDepth(1000);
      
      this.tweens.add({
        targets: heart,
        y: heart.y - 100,
        alpha: 0,
        scale: 1.5,
        duration: 1500,
        delay: i * 100,
        onComplete: () => heart.destroy()
      });
    }
  }

  // ============ NEW ACTION BUTTONS ============
  // Positioned horizontally at bottom, above cards
  
  private createPartyButton(): void {
    // Position: left of center, above cards
    const btn = this.add.container(GAME_WIDTH / 2 - 180, GAME_HEIGHT - 25).setDepth(100);
    
    const bg = this.add.rectangle(0, 0, 50, 40, COLORS.bone, 0.95)
      .setStrokeStyle(2, COLORS.oliveWood);
    const icon = this.add.text(0, -6, '🎉', { fontSize: '16px' }).setOrigin(0.5);
    const label = this.add.text(0, 12, 'PARTY', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '8px',
      color: '#163F29'
    }).setOrigin(0.5);
    const cost = this.add.text(25, -15, '$5k', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '7px',
      color: '#8B5A2B'
    }).setOrigin(0.5);
    
    btn.add([bg, icon, label, cost]);
    btn.setSize(50, 40);
    btn.setInteractive({ useHandCursor: true });
    
    btn.on('pointerover', () => bg.setFillStyle(0xE8E4D8));
    btn.on('pointerout', () => bg.setFillStyle(COLORS.bone));
    
    btn.on('pointerdown', () => {
      if (this.isActionInProgress) {
        this.showEvent('⚠️ Action in progress!');
        return;
      }
      if (ACTION_SYSTEM.actionsRemaining < 1) {
        this.showEvent('⚠️ No actions remaining this year!');
        return;
      }
      if (RESOURCES.funds < 5000) {
        this.showEvent('⚠️ Need $5,000 for a party!');
        return;
      }
      this.throwParty();
    });
    
    this.partyButton = btn;
  }
  
  private throwParty(): void {
    ACTION_SYSTEM.actionsRemaining--;
    RESOURCES.funds -= 5000;
    this.updateActionPoints();
    
    // Start action with progress bar
    this.startActionWithProgress('🎉 Party Time!', 4000, () => {
      // Party heals herd to 80 if below 50
      if (RESOURCES.herdHealth < 50) {
        RESOURCES.herdHealth = 80;
        this.showEvent('🎉 Party boosted herd morale to 80%!');
      } else {
        RESOURCES.herdHealth = Math.min(100, RESOURCES.herdHealth + 10);
        this.showEvent('🎉 Great party! +10 herd health!');
      }
      
      // Reputation boost
      RESOURCES.reputation = Math.min(100, RESOURCES.reputation + 5);
      this.resourceDisplay.update();
      this.addJournalEntry('Threw a party! 🎉');
    });
    
    // Visual: horses dance!
    this.horseDanceParty();
    
    // Music notes and emojis
    this.partyEffects();
  }
  
  private horseDanceParty(): void {
    this.horses.forEach((horse, i) => {
      // Stagger the dance start
      this.time.delayedCall(i * 200, () => {
        if (!horse.active) return;
        
        // Bounce dance
        this.tweens.add({
          targets: horse,
          y: horse.y - 20,
          duration: 300,
          yoyo: true,
          repeat: 6,
          ease: 'Bounce.easeOut'
        });
        
        // Side to side
        this.tweens.add({
          targets: horse,
          x: horse.x + 15,
          duration: 300,
          yoyo: true,
          repeat: 6,
          ease: 'Sine.easeInOut'
        });
      });
    });
  }
  
  private partyEffects(): void {
    const partyEmojis = ['🎉', '🎊', '🎵', '🎶', '💃', '🕺', '✨', '🌟', '🎈', '🥳'];
    
    // Spawn party emojis across screen
    for (let i = 0; i < 25; i++) {
      this.time.delayedCall(i * 150, () => {
        const emoji = this.add.text(
          Phaser.Math.Between(100, GAME_WIDTH - 100),
          Phaser.Math.Between(400, 600),
          Phaser.Math.RND.pick(partyEmojis),
          { fontSize: `${Phaser.Math.Between(20, 36)}px` }
        ).setOrigin(0.5).setDepth(1000);
        
        this.tweens.add({
          targets: emoji,
          y: emoji.y - Phaser.Math.Between(80, 150),
          x: emoji.x + Phaser.Math.Between(-50, 50),
          rotation: Phaser.Math.Between(-1, 1),
          alpha: 0,
          scale: { from: 0.5, to: 1.5 },
          duration: 2000,
          ease: 'Quad.easeOut',
          onComplete: () => emoji.destroy()
        });
      });
    }
  }
  
  // FERTILIZER BUTTON - boost forage for $5k
  private createWaterTroughButton(): void {
    // Position: center, at bottom action bar
    const btn = this.add.container(GAME_WIDTH / 2 - 120, GAME_HEIGHT - 25).setDepth(100);
    
    const bg = this.add.rectangle(0, 0, 50, 40, COLORS.bone, 0.95)
      .setStrokeStyle(2, COLORS.oliveWood);
    const icon = this.add.text(0, -6, '🌱', { fontSize: '16px' }).setOrigin(0.5);
    const label = this.add.text(0, 12, 'FERTILIZE', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '6px',
      color: '#163F29'
    }).setOrigin(0.5);
    const cost = this.add.text(25, -15, '$5k', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '7px',
      color: '#8B5A2B'
    }).setOrigin(0.5);
    
    btn.add([bg, icon, label, cost]);
    btn.setSize(50, 40);
    btn.setInteractive({ useHandCursor: true });
    
    btn.on('pointerover', () => bg.setFillStyle(0xE8E4D8));
    btn.on('pointerout', () => bg.setFillStyle(COLORS.bone));
    
    btn.on('pointerdown', () => {
      if (this.isActionInProgress) {
        this.showEvent('⚠️ Action in progress!');
        return;
      }
      if (ACTION_SYSTEM.actionsRemaining < 1) {
        this.showEvent('⚠️ No actions remaining this year!');
        return;
      }
      if (RESOURCES.funds < 5000) {
        this.showEvent('⚠️ Need $5,000 to fertilize!');
        return;
      }
      this.applyFertilizer();
    });
    
    this.waterTroughButton = btn;
  }
  
  private applyFertilizer(): void {
    // Fertilizer costs $5k and 1 action - boosts forage by 10%
    ACTION_SYSTEM.actionsRemaining--;
    RESOURCES.funds -= 5000;
    this.updateActionPoints();
    
    this.startActionWithProgress('🌱 Spreading Fertilizer', 2000, () => {
      // Boost forage by 10%
      RESOURCES.forage = Math.min(100, RESOURCES.forage + 10);
      
      this.showEvent('🌱 Fertilizer applied! +10% forage');
      this.addJournalEntry('Applied fertilizer 🌱');
      this.resourceDisplay.update();
      
      // Trigger flower bloom effect like rain
      this.spawnFlowers();
    });
  }
  
  private spawnFlowers(): void {
    // Reuse the rain flower effect
    this.spawnFlowersAfterRain();
  }
  
  private createScoutButton(): void {
    // Position: right of center, above cards - now WATER HAUL
    const btn = this.add.container(GAME_WIDTH / 2 - 60, GAME_HEIGHT - 25).setDepth(100);
    
    const bg = this.add.rectangle(0, 0, 50, 40, COLORS.bone, 0.95)
      .setStrokeStyle(2, COLORS.oliveWood);
    const icon = this.add.text(0, -6, '💧', { fontSize: '16px' }).setOrigin(0.5);
    const label = this.add.text(0, 12, 'WATER', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '8px',
      color: '#163F29'
    }).setOrigin(0.5);
    const cost = this.add.text(25, -15, '$5k', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '7px',
      color: '#8B5A2B'
    }).setOrigin(0.5);
    
    btn.add([bg, icon, label, cost]);
    btn.setSize(50, 40);
    btn.setInteractive({ useHandCursor: true });
    
    btn.on('pointerover', () => bg.setFillStyle(0xE8E4D8));
    btn.on('pointerout', () => bg.setFillStyle(COLORS.bone));
    
    btn.on('pointerdown', () => {
      if (this.isActionInProgress) {
        this.showEvent('⚠️ Action in progress!');
        return;
      }
      if (ACTION_SYSTEM.actionsRemaining < 1) {
        this.showEvent('⚠️ No actions remaining this year!');
        return;
      }
      if (RESOURCES.funds < 5000) {
        this.showEvent('⚠️ Need $5,000 to haul water!');
        return;
      }
      this.haulWater();
    });
    
    this.scoutButton = btn;
  }
  
  private haulWater(): void {
    // Water haul costs $5k and 1 action - boosts water by 10%
    ACTION_SYSTEM.actionsRemaining--;
    RESOURCES.funds -= 5000;
    this.updateActionPoints();
    
    this.startActionWithProgress('💧 Hauling Water', 2000, () => {
      // Boost water by 10%
      RESOURCES.water = Math.min(100, RESOURCES.water + 10);
      
      this.showEvent('💧 Water delivered! +10% water');
      this.addJournalEntry('Hauled water 💧');
      this.resourceDisplay.update();
      
      // Water splash effect
      this.spawnWaterSplash();
    });
  }
  
  private spawnWaterSplash(): void {
    // Spawn water droplet emojis near the pond
    const drops = ['💧', '💦', '🌊', '💙'];
    for (let i = 0; i < 8; i++) {
      const x = Phaser.Math.Between(120, 320);
      const y = Phaser.Math.Between(360, 400);
      const drop = this.add.text(x, y, Phaser.Math.RND.pick(drops), {
        fontSize: `${Phaser.Math.Between(14, 22)}px`
      }).setOrigin(0.5).setDepth(25).setAlpha(0);
      
      // Splash animation
      this.tweens.add({
        targets: drop,
        alpha: 1,
        y: y - 20,
        scale: { from: 0.5, to: 1.2 },
        duration: 400,
        delay: i * 80,
        ease: 'Back.easeOut'
      });
      
      // Fade out
      this.time.delayedCall(2000 + i * 80, () => {
        this.tweens.add({
          targets: drop,
          alpha: 0,
          y: drop.y + 30,
          duration: 800,
          onComplete: () => drop.destroy()
        });
      });
    }
  }
  
  // ============ ADOPT BUTTON ============
  
  private createAdoptButton(): void {
    // Position: far right in horizontal row above cards
    const btn = this.add.container(GAME_WIDTH / 2 + 120, GAME_HEIGHT - 25).setDepth(100);
    
    const bg = this.add.rectangle(0, 0, 50, 40, COLORS.bone, 0.95)
      .setStrokeStyle(2, COLORS.oliveWood);
    const icon = this.add.text(0, -6, '🚚', { fontSize: '16px' }).setOrigin(0.5);
    const label = this.add.text(0, 12, 'ADOPT', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '8px',
      color: '#163F29'
    }).setOrigin(0.5);
    
    btn.add([bg, icon, label]);
    btn.setSize(50, 40);
    btn.setInteractive({ useHandCursor: true });
    
    btn.on('pointerover', () => bg.setFillStyle(0xE8E4D8));
    btn.on('pointerout', () => bg.setFillStyle(COLORS.bone));
    
    btn.on('pointerdown', () => {
      if (ACTION_SYSTEM.actionsRemaining < 1) {
        this.showEvent('⚠️ No actions remaining this year!');
        return;
      }
      this.adoptOutHorses();
    });
    
    this.adoptButton = btn;
  }
  
  private adoptOutHorses(): void {
    // Adopt out 20% of horses (balanced for 2 actions/year)
    const adoptCount = Math.max(3, Math.floor(RESOURCES.population * 0.20));
    
    // Check if we have enough horses (need to keep at least 10)
    if (RESOURCES.population - adoptCount < 10) {
      this.showEvent('⚠️ Not enough horses to adopt out!');
      return;
    }
    
    // Costs 1 action
    ACTION_SYSTEM.actionsRemaining--;
    this.updateActionPoints();
    
    // Pick horses to adopt out (only horses, not burros)
    const horsesToAdopt: HorseSprite[] = [];
    const availableHorses = [...this.horses];
    
    for (let i = 0; i < adoptCount && availableHorses.length > 0; i++) {
      const randomIndex = Phaser.Math.Between(0, availableHorses.length - 1);
      horsesToAdopt.push(availableHorses[randomIndex]);
      availableHorses.splice(randomIndex, 1);
    }
    
    const actualAdopted = horsesToAdopt.length;
    const payment = actualAdopted * 800; // $800 per horse
    
    // Animate each horse leaving with staggered timing
    horsesToAdopt.forEach((horse, i) => {
      // Remove from horses array
      const index = this.horses.indexOf(horse);
      if (index > -1) {
        this.horses.splice(index, 1);
      }
      
      // Staggered animation
      this.time.delayedCall(i * 200, () => {
        // Show truck emoji above horse
        const truck = this.add.text(horse.x, horse.y - 40, '🚚', { fontSize: '20px' })
          .setOrigin(0.5).setDepth(1000);
        
        this.tweens.add({
          targets: [horse, truck],
          x: GAME_WIDTH + 100,
          alpha: 0,
          duration: 800,
          ease: 'Power2',
          onComplete: () => {
            horse.destroy();
            truck.destroy();
          }
        });
      });
    });
    
    // Update resources
    RESOURCES.population -= actualAdopted;
    RESOURCES.funds += payment;
    this.resourceDisplay.update();
    
    this.showEvent(`🚚 ${actualAdopted} horses adopted! +$${payment.toLocaleString()}`);
    this.addJournalEntry(`Adopted out ${actualAdopted} horses`);
  }
  
  // ============ BUY CARDS BUTTON ============
  
  private createBuyCardsButton(): void {
    // Position: center of screen, shows only when hand is empty
    const btn = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 50).setDepth(500);
    
    const bg = this.add.rectangle(0, 0, 180, 50, COLORS.deepForest, 0.95)
      .setStrokeStyle(3, COLORS.gold);
    const icon = this.add.text(-65, 0, '🃏', { fontSize: '24px' }).setOrigin(0.5);
    const label = this.add.text(10, -8, 'BUY 3 CARDS', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#FFD700'
    }).setOrigin(0.5);
    const costLabel = this.add.text(10, 10, '15% of funds + 1 action', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '9px',
      color: '#B0B0A0'
    }).setOrigin(0.5);
    
    btn.add([bg, icon, label, costLabel]);
    btn.setSize(180, 50);
    btn.setInteractive({ useHandCursor: true });
    btn.setVisible(false); // Hidden by default
    
    btn.on('pointerover', () => bg.setFillStyle(0x2A5040));
    btn.on('pointerout', () => bg.setFillStyle(COLORS.deepForest));
    
    btn.on('pointerdown', () => {
      const cost = Math.floor(RESOURCES.funds * 0.15);
      if (RESOURCES.funds < 1000) {
        this.showEvent('⚠️ Not enough funds!');
        return;
      }
      if (ACTION_SYSTEM.actionsRemaining < 1) {
        this.showEvent('⚠️ No actions remaining!');
        return;
      }
      if (ACTION_SYSTEM.boughtCardsThisYear) {
        this.showEvent('⚠️ Already bought cards this year!');
        return;
      }
      if (this.hand.length > 0) {
        this.showEvent('⚠️ You still have cards!');
        return;
      }
      this.buyNewCards();
    });
    
    this.buyCardsButton = btn;
  }
  
  private buyNewCards(): void {
    // Cost: 15% of current funds + 1 action
    const cost = Math.floor(RESOURCES.funds * 0.15);
    RESOURCES.funds -= cost;
    ACTION_SYSTEM.actionsRemaining--;
    ACTION_SYSTEM.boughtCardsThisYear = true;
    this.updateActionPoints();
    this.resourceDisplay.update();
    
    // Draw 3 new cards
    for (let i = 0; i < 3; i++) {
      this.time.delayedCall(i * 200, () => {
        this.drawSingleCard();
      });
    }
    
    this.time.delayedCall(700, () => {
      this.rearrangeHand();
      this.updateBuyCardsButton();
    });
    
    this.showEvent(`🃏 Bought 3 cards! -$${cost.toLocaleString()}`);
    this.addJournalEntry('Purchased new cards');
  }
  
  private updateBuyCardsButton(): void {
    if (this.buyCardsButton) {
      // Show only if: hand empty, haven't bought this year, have actions
      const canBuy = this.hand.length === 0 && 
                     !ACTION_SYSTEM.boughtCardsThisYear && 
                     ACTION_SYSTEM.actionsRemaining > 0;
      this.buyCardsButton.setVisible(canBuy);
    }
  }
  
  // ============ ACTION PROGRESS BAR ============
  
  private createActionProgressBar(): void {
    this.actionProgressBar = this.add.container(GAME_WIDTH / 2, 150).setDepth(2000).setVisible(false);
    
    // Background panel - using color scheme
    const panel = this.add.rectangle(0, 0, 300, 60, COLORS.deepForest, 0.95)
      .setStrokeStyle(2, COLORS.oliveWood);
    
    // Progress bar background
    const barBg = this.add.rectangle(0, 10, 260, 20, 0x2A2A2A);
    
    // Progress bar fill (will be animated) - using cornflower blue
    const barFill = this.add.rectangle(-130, 10, 0, 16, COLORS.cornflower).setOrigin(0, 0.5);
    barFill.setName('progressFill');
    
    // Action label
    const label = this.add.text(0, -15, '', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      color: '#D2CFBF' // Bone color
    }).setOrigin(0.5);
    label.setName('progressLabel');
    
    this.actionProgressBar.add([panel, barBg, barFill, label]);
  }
  
  private startActionWithProgress(actionName: string, duration: number, onComplete: () => void): void {
    if (!this.actionProgressBar) return;
    
    this.isActionInProgress = true;
    this.actionProgressBar.setVisible(true);
    
    // Update label
    const label = this.actionProgressBar.getByName('progressLabel') as Phaser.GameObjects.Text;
    if (label) label.setText(actionName);
    
    // Animate progress bar
    const fill = this.actionProgressBar.getByName('progressFill') as Phaser.GameObjects.Rectangle;
    if (fill) {
      fill.width = 0;
      this.tweens.add({
        targets: fill,
        width: 260,
        duration: duration,
        ease: 'Linear',
        onComplete: () => {
          this.isActionInProgress = false;
          this.actionProgressBar?.setVisible(false);
          onComplete();
        }
      });
    }
  }
  
  // ============ FIRE MECHANIC ============
  
  private startFire(count: number = 3): void {
    this.showEvent('🔥 FIRE! Click to extinguish!');
    
    // Fire clears land for regrowth next year (+20% forage bonus)
    this.fireRegrowthBonus = 20;
    
    // Spawn clickable fires
    for (let i = 0; i < count; i++) {
      this.time.delayedCall(i * 300, () => {
        const fire = this.add.text(
          Phaser.Math.Between(150, GAME_WIDTH - 150),
          Phaser.Math.Between(450, 600),
          '🔥',
          { fontSize: '48px' }
        ).setOrigin(0.5).setDepth(1500).setInteractive({ useHandCursor: true });
        
        // Fire flicker animation
        this.tweens.add({
          targets: fire,
          scaleX: { from: 0.9, to: 1.1 },
          scaleY: { from: 0.9, to: 1.1 },
          duration: 200,
          yoyo: true,
          repeat: -1
        });
        
        // Click to extinguish
        fire.on('pointerdown', () => {
          this.extinguishFire(fire);
        });
        
        this.activeFires.push(fire);
      });
    }
    
    // Fire damages resources over time if not extinguished
    this.time.addEvent({
      delay: 2000,
      repeat: 5,
      callback: () => {
        if (this.activeFires.length > 0) {
          RESOURCES.forage = Math.max(0, RESOURCES.forage - 5);
          RESOURCES.herdHealth = Math.max(0, RESOURCES.herdHealth - 3);
          this.resourceDisplay.update();
        }
      }
    });
  }
  
  private extinguishFire(fire: Phaser.GameObjects.Text): void {
    // Costs water - reduced from 5 to 2
    if (RESOURCES.water < 2) {
      this.showEvent('⚠️ Not enough water!');
      return;
    }
    
    RESOURCES.water -= 2;
    this.resourceDisplay.update();
    
    // Remove fire
    const index = this.activeFires.indexOf(fire);
    if (index > -1) {
      this.activeFires.splice(index, 1);
    }
    
    // Extinguish effect
    this.tweens.add({
      targets: fire,
      scale: 0,
      alpha: 0,
      duration: 300,
      onComplete: () => fire.destroy()
    });
    
    // Steam effect
    const steam = this.add.text(fire.x, fire.y, '💨', { fontSize: '32px' })
      .setOrigin(0.5).setDepth(1500);
    this.tweens.add({
      targets: steam,
      y: steam.y - 50,
      alpha: 0,
      duration: 800,
      onComplete: () => steam.destroy()
    });
    
    if (this.activeFires.length === 0) {
      this.showEvent('✅ All fires extinguished!');
    }
  }
  
  private extinguishAllFires(): void {
    // Rain puts out all fires
    this.activeFires.forEach(fire => {
      this.tweens.add({
        targets: fire,
        scale: 0,
        alpha: 0,
        duration: 500,
        onComplete: () => fire.destroy()
      });
    });
    this.activeFires = [];
    
    if (this.activeFires.length > 0) {
      this.showEvent('🌧️ Rain extinguished the fires!');
    }
  }
  
  // ============ WEATHER EFFECTS ============
  
  private startRainEffect(duration: number = 6000): void {
    // Extinguish any active fires
    if (this.activeFires.length > 0) {
      this.extinguishAllFires();
    }
    
    const rainDrops: Phaser.GameObjects.Text[] = [];
    
    // Spawn rain continuously - slower, softer
    const rainEvent = this.time.addEvent({
      delay: 120, // Slower spawn rate
      repeat: Math.floor(duration / 120),
      callback: () => {
        // Vary drop size for realism
        const sizes = ['8px', '10px', '12px', '14px'];
        const size = Phaser.Math.RND.pick(sizes);
        
        const drop = this.add.text(
          Phaser.Math.Between(0, GAME_WIDTH),
          -20,
          '💧',
          { fontSize: size }
        ).setAlpha(Phaser.Math.FloatBetween(0.3, 0.6)).setDepth(900);
        
        rainDrops.push(drop);
        
        // Slower, more realistic fall with gentle drift
        this.tweens.add({
          targets: drop,
          y: GAME_HEIGHT + 20,
          x: drop.x - Phaser.Math.Between(10, 25), // Gentle wind drift
          duration: Phaser.Math.Between(2000, 3500), // Much slower
          ease: 'Linear',
          onComplete: () => {
            drop.destroy();
            const idx = rainDrops.indexOf(drop);
            if (idx > -1) rainDrops.splice(idx, 1);
          }
        });
      }
    });
    
    // Very subtle screen tint
    const rainOverlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x4466AA, 0.08)
      .setDepth(850);
    
    this.time.delayedCall(duration, () => {
      rainEvent.destroy();
      rainOverlay.destroy();
      rainDrops.forEach(d => d.destroy());
      
      // Spawn flowers after rain stops!
      this.spawnFlowersAfterRain();
    });
  }
  
  private spawnFlowersAfterRain(): void {
    // Spawn small flower emojis in the grass area where horses walk
    const flowerTypes = ['🌸', '🌼', '🌺', '🌷', '💮', '🌻'];
    const numFlowers = Phaser.Math.Between(15, 25);
    
    for (let i = 0; i < numFlowers; i++) {
      this.time.delayedCall(i * 100, () => {
        const x = Phaser.Math.Between(100, 800);
        const y = Phaser.Math.Between(420, 550); // Grass area where horses walk
        const flower = Phaser.Math.RND.pick(flowerTypes);
        
        const flowerEmoji = this.add.text(x, y, flower, { 
          fontSize: '12px' 
        }).setOrigin(0.5).setDepth(5).setAlpha(0).setScale(0);
        
        // Pop-in animation
        this.tweens.add({
          targets: flowerEmoji,
          alpha: 0.8,
          scale: 1,
          duration: 400,
          ease: 'Back.easeOut'
        });
        
        // Fade out after a while
        this.time.delayedCall(Phaser.Math.Between(8000, 15000), () => {
          this.tweens.add({
            targets: flowerEmoji,
            alpha: 0,
            scale: 0.5,
            duration: 2000,
            onComplete: () => flowerEmoji.destroy()
          });
        });
      });
    }
  }
  
  private startSnowEffect(duration: number = 8000): void {
    const snowflakes: Phaser.GameObjects.Text[] = [];
    
    // Slower, gentler snow
    const snowEvent = this.time.addEvent({
      delay: 150, // Slower spawn
      repeat: Math.floor(duration / 150),
      callback: () => {
        // Smaller, varied sizes
        const sizes = ['8px', '10px', '12px', '14px', '16px'];
        const size = Phaser.Math.RND.pick(sizes);
        
        const flake = this.add.text(
          Phaser.Math.Between(0, GAME_WIDTH),
          -20,
          Phaser.Math.RND.pick(['❄', '✦', '•', '·']), // Simpler, softer symbols
          { fontSize: size }
        ).setAlpha(Phaser.Math.FloatBetween(0.4, 0.7)).setDepth(900);
        
        snowflakes.push(flake);
        
        // Slow, drifting fall with gentle sway
        const startX = flake.x;
        this.tweens.add({
          targets: flake,
          y: GAME_HEIGHT + 20,
          x: startX + Phaser.Math.Between(-30, 30), // Gentle drift
          rotation: Phaser.Math.FloatBetween(-0.5, 0.5),
          duration: Phaser.Math.Between(4000, 7000), // Much slower
          ease: 'Sine.easeInOut',
          onComplete: () => {
            flake.destroy();
            const idx = snowflakes.indexOf(flake);
            if (idx > -1) snowflakes.splice(idx, 1);
          }
        });
      }
    });
    
    // Very subtle cold overlay
    const snowOverlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xDDDDFF, 0.06)
      .setDepth(850);
    
    this.time.delayedCall(duration, () => {
      snowEvent.destroy();
      snowOverlay.destroy();
      snowflakes.forEach(f => f.destroy());
    });
  }
  
  private updateActionPoints(): void {
    if (this.actionPointsText) {
      this.actionPointsText.setText(`${ACTION_SYSTEM.actionsRemaining}/${ACTION_SYSTEM.actionsPerYear}`);
      
      // Color based on remaining
      if (ACTION_SYSTEM.actionsRemaining === 0) {
        this.actionPointsText.setColor('#FF6347');
      } else if (ACTION_SYSTEM.actionsRemaining === 1) {
        this.actionPointsText.setColor('#FFA500');
      } else {
        this.actionPointsText.setColor('#FFD700');
      }
    }
  }
  
  // ============ CARD EFFECT TRIGGERS ============
  
  private triggerCardEffects(card: CardData): void {
    const title = card.title.toLowerCase();
    const id = card.id.toLowerCase();
    
    // WEATHER EFFECTS
    if (title.includes('rain') || title.includes('good rains') || id.includes('rain')) {
      this.startRainEffect(7000);
      // Rain also extinguishes fires
    }
    
    if (title.includes('winter') || title.includes('snow') || title.includes('harsh') || 
        title.includes('blizzard') || this.season === 'winter') {
      if (card.type === 'event' && (title.includes('winter') || title.includes('harsh'))) {
        this.startSnowEffect(8000);
      }
    }
    
    // FIRE EVENTS
    if (title.includes('fire') || title.includes('wildfire') || id.includes('fire')) {
      this.startFire(Phaser.Math.Between(2, 4));
    }
    
    // CRISIS TRACKING - 2 crises in one year = herd health drops
    if (card.type === 'event' && (
        title.includes('drought') || title.includes('fire') || title.includes('disease') ||
        title.includes('harsh') || title.includes('crisis') || title.includes('emergency')
    )) {
      this.crisisCountThisYear++;
      if (this.crisisCountThisYear >= 2) {
        RESOURCES.herdHealth = Math.min(RESOURCES.herdHealth, 45);
        this.showEvent('⚠️ Multiple crises! Herd morale plummeting!');
        this.horseReaction('scared');
      }
    }
    
    // CELEBRITY/MEDIA EFFECTS
    if (title.includes('celebrity') || title.includes('influencer') || title.includes('media') ||
        title.includes('documentary') || title.includes('viral')) {
      RESOURCES.reputation = Math.min(100, RESOURCES.reputation + 15);
      this.showEvent('📸 Media attention! +15 reputation');
      // Confetti effect
      this.partyEffects();
    }
    
    // VACCINATION/VET EFFECTS
    if (id.includes('pzp') || id.includes('vaccine') || title.includes('vaccin')) {
      RESOURCES.blmRelations = Math.min(100, RESOURCES.blmRelations + 8);
      RESOURCES.herdHealth = Math.min(100, RESOURCES.herdHealth + 10);
      this.showEvent('💉 Vaccination complete! +8 BLM relations, +10 health');
    }
    
    // TOUR/VISITOR EFFECTS
    if (title.includes('tour') || title.includes('visitor') || title.includes('education')) {
      RESOURCES.reputation = Math.min(100, RESOURCES.reputation + 8);
      RESOURCES.funds += 500;
      this.showEvent('🎒 Visitors loved it! +8 reputation, +$500');
    }
    
    // PARTNERSHIP/BLM EFFECTS
    if (title.includes('partnership') || title.includes('blm') || title.includes('government')) {
      RESOURCES.blmRelations = Math.min(100, RESOURCES.blmRelations + 12);
      this.showEvent('🤝 Partnership strengthened! +12 BLM relations');
    }
    
    // DROUGHT EFFECTS
    if (title.includes('drought')) {
      RESOURCES.water = Math.max(0, RESOURCES.water - 20);
      RESOURCES.forage = Math.max(0, RESOURCES.forage - 15);
      this.showEvent('☀️ Drought conditions! Water and forage depleted');
      this.horseReaction('sad');
    }
    
    // DISEASE EFFECTS
    if (title.includes('disease') || title.includes('illness') || title.includes('sick')) {
      RESOURCES.herdHealth = Math.max(0, RESOURCES.herdHealth - 20);
      this.showEvent('🤒 Disease outbreak! Herd health declining');
      this.horseReaction('sad');
    }
    
    // BURRO SPAWN
    if (card.spawnBurro) {
      this.spawnBurro();
      this.showAlertBox('🫏', 'A burro has joined the herd!', 3500);
    }
    
    // ADOPTION EFFECTS - animate horses leaving
    if (id.includes('adoption') || title.includes('adoption') || id.includes('gentling')) {
      // Calculate how many were adopted (based on card effect)
      let adoptedCount = 0;
      if (id === 'adoption_drive') {
        adoptedCount = Math.min(3, Math.max(0, RESOURCES.population - 20)); // Adopt up to 3, keep min 20
      } else if (id === 'gentling_program') {
        adoptedCount = 2; // Gentling adopts 2
      }
      if (adoptedCount > 0) {
        this.removeVisualHorses(adoptedCount);
        this.showEvent(`🚚 ${adoptedCount} horse${adoptedCount > 1 ? 's' : ''} found loving homes!`);
      }
    }
  }
  
  // ============ WEATHER & CELESTIAL ============
  
  private createCelestialBody(): void {
    this.celestialBody = this.add.container(70, 70).setDepth(1);
    
    // Glow effect
    const glow = this.add.circle(0, 0, 28, 0xFFFFFF, 0.15);
    const glow2 = this.add.circle(0, 0, 22, 0xFFFFFF, 0.2);
    
    // Main body
    const body = this.add.circle(0, 0, 16, 0xFFD700);
    
    this.celestialBody.add([glow, glow2, body]);
    
    // Update based on time
    this.updateCelestialBody();
    
    // Subtle pulse animation
    this.tweens.add({
      targets: [glow, glow2],
      alpha: { from: 0.1, to: 0.3 },
      scale: { from: 1, to: 1.1 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }
  
  private updateCelestialBody(): void {
    if (!this.celestialBody) return;
    
    const body = this.celestialBody.getAt(2) as Phaser.GameObjects.Arc;
    const glow = this.celestialBody.getAt(0) as Phaser.GameObjects.Arc;
    const glow2 = this.celestialBody.getAt(1) as Phaser.GameObjects.Arc;
    
    if (this.currentTimeOfDay === 'night') {
      // Moon - silver/white
      body.setFillStyle(0xE8E8F0);
      glow.setFillStyle(0xCCCCFF, 0.15);
      glow2.setFillStyle(0xCCCCFF, 0.2);
    } else if (this.currentTimeOfDay === 'morning') {
      // Rising sun - orange/gold
      body.setFillStyle(0xFFA500);
      glow.setFillStyle(0xFFD700, 0.2);
      glow2.setFillStyle(0xFFA500, 0.25);
    } else {
      // Day sun - bright yellow
      body.setFillStyle(0xFFD700);
      glow.setFillStyle(0xFFFF00, 0.15);
      glow2.setFillStyle(0xFFD700, 0.2);
    }
  }
  
  // ============ POND DUCKS ============
  
  private createPondDucks(): void {
    // Ducks appear every 2 minutes, swim across pond, then fade out
    // First spawn after a short delay
    this.time.delayedCall(5000, () => this.spawnDuckFamily());
    
    // Then spawn every 2 minutes
    this.time.addEvent({
      delay: 120000, // 2 minutes
      loop: true,
      callback: () => this.spawnDuckFamily()
    });
  }
  
  private spawnDuckFamily(): void {
    // Create container for duck family
    const duckContainer = this.add.container(80, 0);
    duckContainer.setDepth(5);
    
    // Create mama duck and ducklings - positioned in the pond (y ~398)
    // Duck emoji faces LEFT by default, so flip it to face RIGHT (direction of travel)
    const ducks = this.add.text(0, 398, '🦆', { fontSize: '14px' }).setOrigin(0.5).setFlipX(true);
    const duckling1 = this.add.text(-18, 400, '🐤', { fontSize: '10px' }).setOrigin(0.5); // Ducklings follow behind
    const duckling2 = this.add.text(-32, 399, '🐤', { fontSize: '9px' }).setOrigin(0.5);
    const duckling3 = this.add.text(-44, 401, '🐤', { fontSize: '9px' }).setOrigin(0.5);
    
    duckContainer.add([ducks, duckling1, duckling2, duckling3]);
    
    // Make ducks clickable - adds +1 forage
    duckContainer.setSize(60, 30);
    duckContainer.setInteractive({ useHandCursor: true });
    duckContainer.on('pointerdown', () => {
      RESOURCES.forage = Math.min(100, RESOURCES.forage + 1);
      this.resourceDisplay.update();
      
      // Show +1 feedback
      const bonus = this.add.text(duckContainer.x + 20, 380, '+1 🌿', {
        fontSize: '14px',
        color: '#90EE90',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(0.5).setDepth(100);
      
      this.tweens.add({
        targets: bonus,
        y: bonus.y - 30,
        alpha: 0,
        duration: 1000,
        onComplete: () => bonus.destroy()
      });
      
      // Ducks quack and scatter
      this.tweens.add({
        targets: duckContainer,
        y: duckContainer.y - 10,
        duration: 200,
        yoyo: true
      });
    });
    
    // Fade in
    duckContainer.setAlpha(0);
    this.tweens.add({
      targets: duckContainer,
      alpha: 1,
      duration: 1000
    });
    
    // Swim across pond from left to right (facing right)
    this.tweens.add({
      targets: duckContainer,
      x: 320, // Swim to right side of pond
      duration: 25000, // 25 seconds to cross
      ease: 'Linear',
      onComplete: () => {
        // Fade out when reaching the end
        this.tweens.add({
          targets: duckContainer,
          alpha: 0,
          duration: 2000,
          onComplete: () => duckContainer.destroy()
        });
      }
    });
    
    // Subtle bobbing while swimming
    this.tweens.add({
      targets: duckContainer,
      y: 5,
      duration: 1500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: 16 // Enough repeats for the swim duration
    });
  }
  
  // ============ ALERT BOX ============
  
  private showAlertBox(emoji: string, message: string, duration: number = 3000): void {
    // Remove existing alert if any
    if (this.alertBox) {
      this.alertBox.destroy();
    }
    
    // Create alert box at top center
    this.alertBox = this.add.container(GAME_WIDTH / 2, 80);
    this.alertBox.setDepth(2000);
    
    const bgWidth = Math.max(200, message.length * 10 + 60);
    const bg = this.add.rectangle(0, 0, bgWidth, 50, 0x1A2F1A, 0.95)
      .setStrokeStyle(2, 0xD2CFBF);
    
    const emojiText = this.add.text(-bgWidth/2 + 30, 0, emoji, {
      fontSize: '28px'
    }).setOrigin(0.5);
    
    const msgText = this.add.text(10, 0, message, {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '16px',
      color: '#FFFFFF',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    this.alertBox.add([bg, emojiText, msgText]);
    
    // Slide in animation
    this.alertBox.setY(-50);
    this.tweens.add({
      targets: this.alertBox,
      y: 80,
      duration: 400,
      ease: 'Back.easeOut'
    });
    
    // Fade out after duration
    this.time.delayedCall(duration, () => {
      if (this.alertBox) {
        this.tweens.add({
          targets: this.alertBox,
          y: -50,
          alpha: 0,
          duration: 300,
          ease: 'Power2',
          onComplete: () => {
            this.alertBox?.destroy();
            this.alertBox = undefined;
          }
        });
      }
    });
  }
  
  private startWeatherEffects(): void {
    // Random weather based on season
    const weatherRoll = Math.random();
    
    if (this.season === 'winter' && weatherRoll < 0.4) {
      this.startSnowfall();
    } else if ((this.season === 'spring' || this.season === 'autumn') && weatherRoll < 0.25) {
      this.startRainfall();
    }
    // Summer and other times - clear weather, no particles
  }
  
  private startSnowfall(): void {
    // Stop any existing ambient weather
    this.stopAmbientWeather();
    
    const snowEmojis = ['❄️', '❄', '✨', '·'];
    const sizes = ['10px', '14px', '18px', '8px', '12px'];
    
    // Create snowflakes periodically - limited duration
    this.ambientWeatherEvent = this.time.addEvent({
      delay: 300,
      repeat: 30, // Only 30 snowflakes, then stop
      callback: () => {
        if (this.weatherParticles.length > 30) return;
        
        const x = Phaser.Math.Between(0, GAME_WIDTH);
        const emoji = Phaser.Math.RND.pick(snowEmojis);
        const size = Phaser.Math.RND.pick(sizes);
        
        const snow = this.add.text(x, -20, emoji, { fontSize: size })
          .setDepth(2)
          .setAlpha(0.7);
        
        this.weatherParticles.push(snow);
        
        // Fall animation with drift
        this.tweens.add({
          targets: snow,
          y: GAME_HEIGHT + 20,
          x: x + Phaser.Math.Between(-50, 50),
          duration: Phaser.Math.Between(4000, 8000),
          ease: 'Linear',
          onComplete: () => {
            snow.destroy();
            const idx = this.weatherParticles.indexOf(snow);
            if (idx > -1) this.weatherParticles.splice(idx, 1);
          }
        });
      }
    });
  }
  
  private startRainfall(): void {
    // Stop any existing ambient weather
    this.stopAmbientWeather();
    
    const rainChars = ['💧', '·', '|'];
    
    this.ambientWeatherEvent = this.time.addEvent({
      delay: 100,
      repeat: 40, // Only 40 drops, then stop
      callback: () => {
        if (this.weatherParticles.length > 50) return;
        
        const x = Phaser.Math.Between(0, GAME_WIDTH);
        const char = Phaser.Math.RND.pick(rainChars);
        
        const rain = this.add.text(x, -10, char, { 
          fontSize: char === '💧' ? '12px' : '16px',
          color: '#87CEEB'
        }).setDepth(2).setAlpha(0.6);
        
        this.weatherParticles.push(rain);
        
        this.tweens.add({
          targets: rain,
          y: GAME_HEIGHT + 10,
          x: x + Phaser.Math.Between(-20, 20),
          duration: Phaser.Math.Between(1000, 2000),
          ease: 'Linear',
          onComplete: () => {
            rain.destroy();
            const idx = this.weatherParticles.indexOf(rain);
            if (idx > -1) this.weatherParticles.splice(idx, 1);
          }
        });
      }
    });
  }
  
  private stopAmbientWeather(): void {
    if (this.ambientWeatherEvent) {
      this.ambientWeatherEvent.destroy();
      this.ambientWeatherEvent = undefined;
    }
    // Clear existing particles
    this.weatherParticles.forEach(p => p.destroy());
    this.weatherParticles = [];
  }

  private createEndYearButton(): void {
    // Position BELOW the journal
    this.endYearButton = this.add.container(GAME_WIDTH - 90, 280);
    
    const bg = this.add.rectangle(0, 0, 100, 45, COLORS.oliveWood)
      .setStrokeStyle(2, COLORS.bone);
    
    const text = this.add.text(0, -5, 'END YEAR', {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#f4e4bc'
    }).setOrigin(0.5);
    
    const yearText = this.add.text(0, 12, `→ ${RESOURCES.year + 1}`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '11px',
      color: '#d4c4a4'
    }).setOrigin(0.5);
    
    this.endYearButton.add([bg, text, yearText]);
    this.endYearButton.setSize(100, 45);
    this.endYearButton.setInteractive({ useHandCursor: true });
    this.endYearButton.setDepth(60);
    
    this.endYearButton.on('pointerover', () => {
      bg.setFillStyle(0xa06a3b);
    });
    
    this.endYearButton.on('pointerout', () => {
      bg.setFillStyle(COLORS.oliveWood);
    });
    
    this.endYearButton.on('pointerdown', () => {
      // Check if player has unresolved cards
      if (this.hand.length > 0) {
        this.showHandResolutionWarning(yearText);
      } else {
        this.endYear();
        yearText.setText(`→ ${RESOURCES.year + 1}`);
      }
    });
  }
  
  // Show warning about unplayed cards and their consequences
  private showHandResolutionWarning(yearText: Phaser.GameObjects.Text): void {
    const hasCrisis = this.hand.some(card => (card as any).cardData?.isCrisis);
    
    if (hasCrisis) {
      // Cannot skip year with crisis cards!
      this.showEvent('⚠️ Crisis cards must be played!');
      return;
    }
    
    // Calculate penalty for discarding remaining cards
    const discardPenalty = this.hand.length * 5; // -5 reputation per discarded card
    const fundsPenalty = this.hand.length * 500; // -$500 per discarded card (wasted opportunities)
    
    // Show confirmation modal
    this.showDiscardConfirmation(discardPenalty, fundsPenalty, yearText);
  }
  
  private showDiscardConfirmation(repPenalty: number, fundsPenalty: number, yearText: Phaser.GameObjects.Text): void {
    const modal = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT / 2).setDepth(2000);
    
    const bg = this.add.rectangle(0, 0, 380, 200, 0x1a1a2e, 0.95)
      .setStrokeStyle(2, COLORS.bone);
    
    const title = this.add.text(0, -70, '⚠️ Unplayed Cards', {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#FFD700'
    }).setOrigin(0.5);
    
    const cardCount = this.hand.length;
    const desc = this.add.text(0, -30, `You have ${cardCount} unplayed card${cardCount > 1 ? 's' : ''}.`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    const penalty = this.add.text(0, 0, `Discarding will cost:\n-${repPenalty} Reputation, -$${fundsPenalty}`, {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '13px',
      color: '#FF9999',
      align: 'center'
    }).setOrigin(0.5);
    
    // Discard & Continue button
    const discardBtn = this.add.rectangle(-80, 60, 130, 35, COLORS.danger)
      .setStrokeStyle(1, COLORS.bone)
      .setInteractive({ useHandCursor: true });
    const discardText = this.add.text(-80, 60, 'Discard & End', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '12px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    // Cancel button
    const cancelBtn = this.add.rectangle(80, 60, 100, 35, COLORS.deepForest)
      .setStrokeStyle(1, COLORS.bone)
      .setInteractive({ useHandCursor: true });
    const cancelText = this.add.text(80, 60, 'Go Back', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '12px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    modal.add([bg, title, desc, penalty, discardBtn, discardText, cancelBtn, cancelText]);
    
    discardBtn.on('pointerdown', () => {
      // Apply penalties
      RESOURCES.reputation = Math.max(0, RESOURCES.reputation - repPenalty);
      RESOURCES.funds = Math.max(0, RESOURCES.funds - fundsPenalty);
      
      // Discard all cards
      this.hand.forEach(card => {
        GAME_STATE.discardPile.push((card as any).cardData);
        card.destroy();
      });
      this.hand = [];
      
      this.addJournalEntry(`Discarded cards (-${repPenalty} rep)`);
      modal.destroy();
      
      this.endYear();
      yearText.setText(`→ ${RESOURCES.year + 1}`);
    });
    
    cancelBtn.on('pointerdown', () => {
      modal.destroy();
    });
    
    // Hover effects
    discardBtn.on('pointerover', () => discardBtn.setFillStyle(0xC06060));
    discardBtn.on('pointerout', () => discardBtn.setFillStyle(COLORS.danger));
    cancelBtn.on('pointerover', () => cancelBtn.setFillStyle(0x2A5A3A));
    cancelBtn.on('pointerout', () => cancelBtn.setFillStyle(COLORS.deepForest));
  }

  private endYear(): void {
    GAME_STATE.yearsPlayed++;
    
    // Reset action points for new year
    ACTION_SYSTEM.actionsRemaining = ACTION_SYSTEM.actionsPerYear;
    ACTION_SYSTEM.cardsPlayedThisYear = 0;
    ACTION_SYSTEM.boughtCardsThisYear = false;
    this.updateActionPoints();
    
    // Reset crisis counter for new year
    this.crisisCountThisYear = 0;
    
    // Reduce breeding cooldown
    if (ACTION_SYSTEM.breedingCooldown > 0) {
      ACTION_SYSTEM.breedingCooldown--;
    }
    
    // 1. Auto foals (with diversity bonus)
    const diversityBonus = RESOURCES.diversity > 70 ? 0.03 : (RESOURCES.diversity < 30 ? -0.05 : 0);
    const effectiveFoalRate = RESOURCES.foalRate + diversityBonus;
    const foals = Math.floor(RESOURCES.population * effectiveFoalRate);
    RESOURCES.population += foals;
    GAME_STATE.totalFoalsBorn += foals;
    
    // Foal announcement with emoji celebration
    if (foals > 0) {
      const foalEmojis = foals > 5 ? '🐴🐴🐴' : (foals > 2 ? '🐴🐴' : '🐴');
      this.showEvent(`${foalEmojis} ${foals} foals born!`);
      this.addJournalEntry(`${foals} foals born!`);
      
      // Horse reactions - make them jump!
      this.horseReaction('happy');
    }
    
    // 2. Forage consumption - scales with population
    // Base: 8-12 for small herds, 15-25 for medium, 25-40 for large
    const forageEaten = Math.floor(8 + (RESOURCES.population * 0.25));
    RESOURCES.forage = Math.max(0, RESOURCES.forage - forageEaten);
    
    // 3. Water consumption - scales with population  
    // Base: 5-8 for small herds, 10-18 for medium, 18-30 for large
    const waterDrunk = Math.floor(5 + (RESOURCES.population * 0.18));
    RESOURCES.water = Math.max(0, RESOURCES.water - waterDrunk);
    
    // 4. Natural recovery (seasonal) - reduced to make management matter
    const seasonalRecovery = this.getSeasonalRecovery();
    
    // Apply fire regrowth bonus (land cleared by fire grows back stronger)
    let forageRecovery = seasonalRecovery.forage;
    if (this.fireRegrowthBonus > 0) {
      forageRecovery += this.fireRegrowthBonus;
      this.showEvent('🌱 Fire-cleared land regrows! +20% forage bonus');
      this.fireRegrowthBonus = 0; // Reset after applying
    }
    
    RESOURCES.forage = Math.min(100, RESOURCES.forage + forageRecovery);
    RESOURCES.water = Math.min(100, RESOURCES.water + seasonalRecovery.water);
    
    // Warning messages for low resources
    if (RESOURCES.forage < 25) {
      this.showEvent('⚠️ Forage running low!');
    }
    if (RESOURCES.water < 25) {
      this.showEvent('⚠️ Water sources drying up!');
    }
    
    // 5. Diversity drift (slowly decreases without new blood)
    RESOURCES.diversity = Math.max(0, RESOURCES.diversity - 2);
    
    // 5b. Health impacts from resource scarcity
    if (RESOURCES.forage < 30) {
      const healthLoss = Math.floor((30 - RESOURCES.forage) / 3);
      RESOURCES.herdHealth = Math.max(0, RESOURCES.herdHealth - healthLoss);
      if (healthLoss > 5) this.showEvent('⚠️ Horses weakening from hunger!');
    }
    if (RESOURCES.water < 30) {
      const healthLoss = Math.floor((30 - RESOURCES.water) / 3);
      RESOURCES.herdHealth = Math.max(0, RESOURCES.herdHealth - healthLoss);
      if (healthLoss > 5) this.showEvent('⚠️ Dehydration affecting herd!');
    }
    
    // 5c. Overpopulation stress (above AML of 120)
    if (RESOURCES.population > 120) {
      const overPop = RESOURCES.population - 120;
      RESOURCES.herdHealth = Math.max(0, RESOURCES.herdHealth - Math.floor(overPop / 5));
      RESOURCES.forage = Math.max(0, RESOURCES.forage - Math.floor(overPop / 3));
      this.showEvent('⚠️ Overpopulation straining resources!');
    }
    
    // 5d. Natural health recovery if resources are good
    if (RESOURCES.forage > 60 && RESOURCES.water > 60 && RESOURCES.herdHealth < 100) {
      RESOURCES.herdHealth = Math.min(100, RESOURCES.herdHealth + 5);
    }
    
    // 6. Random event (45% chance - more events!)
    if (Math.random() < 0.45) {
      this.triggerRandomEvent();
    }
    
    // 7. Check for choice events
    this.checkChoiceEvents();
    
    // 8. Annual BLM funding (scales with relations)
    const blmFunding = 3000 + Math.floor(RESOURCES.blmRelations * 40);
    RESOURCES.funds += blmFunding;
    
    RESOURCES.year++;
    this.updateSeasonDisplay();
    this.updateYearCounter();
    
    // Age up all horses and update their scales
    this.horses.forEach(horse => horse.ageUp(RESOURCES.year));
    
    // Transition day/night cycle
    this.transitionTimeOfDay();
    
    // Check win/lose conditions
    this.checkGameState();
    
    // Check for paint horse unlock (reputation >= 70)
    this.checkPaintUnlock();
    
    // Screen fade BEFORE drawing new cards
    const fadeOverlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0)
      .setDepth(2000);
    
    this.tweens.add({
      targets: fadeOverlay,
      alpha: 0.6,
      duration: 400,
      yoyo: true,
      hold: 200,
      onComplete: () => {
        fadeOverlay.destroy();
      }
    });
    
    // Delay card draw until after fade
    this.time.delayedCall(500, () => {
      // Update visuals
      this.updateHorseCount();
      this.resourceDisplay.update();
      this.drawHand();
    });
    
    // Reset foal rate if it was modified (base 12%)
    RESOURCES.foalRate = 0.12;
  }

  private getSeasonalRecovery(): { forage: number; water: number } {
    // Reduced recovery rates to make resource management more challenging
    switch (this.season) {
      case 'spring': return { forage: 18, water: 14 }; // Best season
      case 'summer': return { forage: 10, water: 5 };  // Hot & dry
      case 'autumn': return { forage: 12, water: 10 }; // Moderate
      case 'winter': return { forage: 3, water: 3 };   // Harsh!
      default: return { forage: 10, water: 8 };
    }
  }

  // Check if paint horses should be unlocked (reputation >= 70)
  private checkPaintUnlock(): void {
    if (RESOURCES.reputation >= 70 && !isPaintUnlocked()) {
      unlockPaint();
      this.showEvent('🎨 Paint horses unlocked! High reputation attracts rare horses!');
      this.addJournalEntry('Paint horses unlocked!');
    }
  }

  private horseReaction(type: 'happy' | 'sad' | 'scared'): void {
    this.horses.forEach((horse, i) => {
      this.time.delayedCall(i * 100, () => {
        if (type === 'happy') {
          // Jump animation
          this.tweens.add({
            targets: horse,
            y: horse.y - 20,
            duration: 200,
            yoyo: true,
            ease: 'Bounce.easeOut'
          });
        } else if (type === 'scared') {
          // Scatter animation
          this.tweens.add({
            targets: horse,
            x: horse.x + Phaser.Math.Between(-50, 50),
            duration: 300,
            ease: 'Power2'
          });
        }
      });
    });
  }

  private checkChoiceEvents(): void {
    for (const event of choiceEvents) {
      if (event.condition && event.condition(RESOURCES)) {
        // 20% chance to trigger if condition met
        if (Math.random() < 0.2) {
          this.showChoiceEvent(event);
          break;
        }
      }
    }
  }

  private showChoiceEvent(event: ChoiceEventData): void {
    this.choiceModal.removeAll(true);
    this.choiceModal.setVisible(true);
    
    const bg = this.add.rectangle(0, 0, 500, 350, 0x1a1a2e, 0.95)
      .setStrokeStyle(3, 0xffd700);
    
    const art = this.add.text(0, -120, event.art, { fontSize: '60px' }).setOrigin(0.5);
    const title = this.add.text(0, -60, event.title, {
      fontFamily: 'Lora, Georgia, serif',
      fontSize: '24px',
      color: '#ffd700',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5);
    
    const desc = this.add.text(0, -10, event.description, {
      fontFamily: 'Source Sans 3, Georgia, sans-serif',
      fontSize: '16px',
      color: '#ffffff',
      wordWrap: { width: 400 },
      align: 'center',
      stroke: '#000000',
      strokeThickness: 1
    }).setOrigin(0.5);
    
    this.choiceModal.add([bg, art, title, desc]);
    
    // Add choice buttons
    event.choices.forEach((choice, i) => {
      const btn = this.add.container(0, 60 + i * 50);
      const btnBg = this.add.rectangle(0, 0, 400, 40, 0x4a3728)
        .setStrokeStyle(2, 0x8b5a2b)
        .setInteractive({ useHandCursor: true });
      const btnText = this.add.text(0, 0, choice.text, {
        fontFamily: 'Georgia',
        fontSize: '14px',
        color: '#f4e4bc'
      }).setOrigin(0.5);
      
      btn.add([btnBg, btnText]);
      this.choiceModal.add(btn);
      
      btnBg.on('pointerover', () => btnBg.setFillStyle(0x6a5748));
      btnBg.on('pointerout', () => btnBg.setFillStyle(0x4a3728));
      btnBg.on('pointerdown', () => {
        choice.effect(RESOURCES);
        this.showEvent(choice.result);
        this.addJournalEntry(choice.result.substring(0, 30));
        this.choiceModal.setVisible(false);
        this.resourceDisplay.update();
        this.updateHorseCount();
      });
    });
  }

  private triggerRandomEvent(): void {
    // 60% positive events, 40% negative (gentler balance)
    const isPositive = Math.random() < 0.6;
    
    // Combine all event pools
    const positiveEvents = [
      ...sillyEventCards.filter(c => c.type === 'event'),
      ...seasonalEventCards,
      { id: 'good_rains', title: 'Good Rains', art: '🌧️🌈', effect: (r: GameResources) => { r.water = Math.min(100, r.water + 20); r.forage = Math.min(100, r.forage + 15); } },
      { id: 'donation', title: 'Anonymous Donation', art: '💰✨', effect: (r: GameResources) => { r.funds += 3500; } },
      { id: 'volunteer', title: 'Volunteer Help', art: '🤝', effect: (r: GameResources) => { r.funds += 2000; } },
    ];
    
    const negativeEvents = [
      { id: 'drought', title: 'Drought', art: '☀️🔥', effect: (r: GameResources) => { r.forage = Math.max(0, r.forage - 20); r.water = Math.max(0, r.water - 18); r.herdHealth = Math.max(0, r.herdHealth - 8); }, reaction: 'sad' as const },
      { id: 'harsh_winter', title: 'Harsh Winter', art: '❄️', effect: (r: GameResources) => { r.population = Math.max(1, Math.floor(r.population * 0.92)); r.herdHealth = Math.max(0, r.herdHealth - 12); }, reaction: 'scared' as const },
      { id: 'mountain_lion', title: 'Mountain Lion Attack!', art: '🦁', effect: (r: GameResources) => { const foalsLost = Math.max(1, Math.floor(r.population * 0.08)); r.population = Math.max(1, r.population - foalsLost); r.herdHealth = Math.max(0, r.herdHealth - 10); }, reaction: 'scared' as const },
      { id: 'coyote', title: 'Coyote Pack', art: '🐺', effect: (r: GameResources) => { r.population = Math.max(1, r.population - 1); r.herdHealth = Math.max(0, r.herdHealth - 5); }, reaction: 'scared' as const },
      { id: 'disease', title: 'Illness Spreads', art: '🤒', effect: (r: GameResources) => { r.herdHealth = Math.max(0, r.herdHealth - 15); r.population = Math.max(1, r.population - Math.floor(r.population * 0.05)); }, reaction: 'sad' as const },
      { id: 'budget', title: 'Budget Cuts', art: '📉', effect: (r: GameResources) => { r.funds = Math.max(0, r.funds - 3000); r.blmRelations = Math.max(0, r.blmRelations - 5); }, reaction: 'sad' as const },
      { id: 'bad_press', title: 'Negative Press', art: '📰', effect: (r: GameResources) => { r.reputation = Math.max(0, r.reputation - 12); r.funds = Math.max(0, r.funds - 1500); }, reaction: 'sad' as const },
      { id: 'fence_break', title: 'Fence Break', art: '🚧', effect: (r: GameResources) => { r.population = Math.max(1, r.population - 2); r.funds = Math.max(0, r.funds - 1000); }, reaction: 'scared' as const },
    ];
    
    const eventPool = isPositive ? positiveEvents : negativeEvents;
    const event = Phaser.Math.RND.pick(eventPool);
    
    if (event && 'effect' in event) {
      event.effect(RESOURCES);
      
      // Clamp values
      RESOURCES.forage = Math.max(0, Math.min(100, RESOURCES.forage));
      RESOURCES.water = Math.max(0, Math.min(100, RESOURCES.water));
      RESOURCES.population = Math.max(0, RESOURCES.population);
      RESOURCES.diversity = Math.max(0, Math.min(100, RESOURCES.diversity));
      RESOURCES.herdHealth = Math.max(0, Math.min(100, RESOURCES.herdHealth));
      RESOURCES.reputation = Math.max(0, Math.min(100, RESOURCES.reputation));
      RESOURCES.blmRelations = Math.max(0, Math.min(100, RESOURCES.blmRelations));
      
      this.resourceDisplay.update();
      
      const title = 'title' in event ? event.title : 'Event';
      const art = 'art' in event ? event.art : '📢';
      const id = 'id' in event ? event.id : '';
      
      // Use alert box for important events
      const importantEvents = ['mountain_lion', 'disease', 'harsh_winter', 'burro_bff', 'drought', 'wildfire'];
      if (importantEvents.includes(id)) {
        this.showAlertBox(art, title, 4000);
      } else {
        this.showEvent(`${art} ${title}!`);
      }
      this.addJournalEntry(title);
      
      // Horse reactions for negative events
      if (!isPositive && 'reaction' in event) {
        const reaction = (event as { reaction: 'happy' | 'sad' | 'scared' }).reaction;
        this.horseReaction(reaction);
      } else if (isPositive) {
        this.horseReaction('happy');
      }
    }
  }

  private showEvent(message: string): void {
    this.eventText.setText(message);
    this.eventText.setAlpha(1);
    
    this.tweens.add({
      targets: this.eventText,
      y: 180,
      alpha: 0,
      duration: 2000,
      ease: 'Power2',
      onComplete: () => {
        this.eventText.y = 200;
      }
    });
  }

  private checkGameState(): void {
    // === LOSE CONDITIONS ===
    if (RESOURCES.population <= 0) {
      this.showGameOver('💀 The herd has been lost...', 'extinction');
      return;
    }
    
    if (RESOURCES.population < 9) {
      // Very low population = genetic collapse
      RESOURCES.diversity = Math.max(0, RESOURCES.diversity - 30);
      this.showGameOver('🧬 Population below 9 - genetic diversity collapsed!', 'genetic_collapse');
      return;
    }
    
    if (RESOURCES.forage <= 0 && RESOURCES.population > 20) {
      this.showGameOver('🥀 Starvation crisis - herd collapsed', 'starvation');
      return;
    }
    
    if (RESOURCES.water <= 0 && RESOURCES.population > 15) {
      this.showGameOver('💧 Drought crisis - herd perished', 'drought');
      return;
    }
    
    if (RESOURCES.population > AML_HIGH * 1.8) {
      this.showGameOver('⚠️ Severe overpopulation - BLM emergency roundup', 'overpop');
      return;
    }
    
    if (RESOURCES.herdHealth <= 10) {
      this.showGameOver('🏥 Disease outbreak - herd devastated', 'disease');
      return;
    }
    
    if (RESOURCES.reputation <= 5) {
      this.showGameOver('📰 Public backlash - program shut down', 'reputation');
      return;
    }
    
    if (RESOURCES.blmRelations <= 5) {
      this.showGameOver('🏛️ BLM revoked management rights', 'blm');
      return;
    }
    
    if (RESOURCES.funds < -3000) {
      this.showGameOver('💸 Bankruptcy - program cancelled', 'bankrupt');
      return;
    }
    
    // Extended play timeout - 20 years from 1990 = 2010
    if (RESOURCES.year >= END_YEAR) {
      this.showGameOver('⏰ Time\'s up! 20-year management period ended.', 'timeout');
      return;
    }
    
    // === WIN CONDITIONS - Must meet ALL criteria ===
    // Victory: AML range (90-120) by end year with good stats
    if (RESOURCES.year >= END_YEAR - 2 &&
        RESOURCES.population >= AML_LOW && 
        RESOURCES.population <= AML_HIGH &&
        RESOURCES.diversity >= 50 &&
        RESOURCES.herdHealth >= 60 &&
        RESOURCES.reputation >= 40 &&
        RESOURCES.blmRelations >= 40 &&
        RESOURCES.funds >= 0) {
      this.showVictory();
      return;
    }
    
    // Partial success - reached 2030 but not all criteria met
    if (RESOURCES.year >= 2030) {
      this.showPartialSuccess();
      return;
    }
  }

  private showGameOver(message: string, reason: string = 'unknown'): void {
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85);
    overlay.setDepth(2000);
    
    const container = this.add.container(GAME_WIDTH / 2, 0).setDepth(2001);
    
    // Title
    const title = this.add.text(0, 100, '💔 GAME OVER 💔', {
      fontFamily: 'Georgia',
      fontSize: '42px',
      color: '#ff4444'
    }).setOrigin(0.5);
    
    // Message
    const msg = this.add.text(0, 160, message, {
      fontFamily: 'Georgia',
      fontSize: '20px',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    // Final stats panel
    const statsBg = this.add.rectangle(0, 300, 400, 180, 0x1a1a2e, 0.9)
      .setStrokeStyle(2, 0x666666);
    
    const statsTitle = this.add.text(0, 230, '📊 Final Statistics', {
      fontFamily: 'Georgia',
      fontSize: '18px',
      color: '#aaaaaa'
    }).setOrigin(0.5);
    
    const stats = [
      `🐴 Herd Size: ${RESOURCES.population} (Goal: ${AML_LOW}-${AML_HIGH})`,
      `🧬 Diversity: ${RESOURCES.diversity}% (Need: 50%+)`,
      `❤️ Herd Health: ${RESOURCES.herdHealth}% (Need: 60%+)`,
      `⭐ Reputation: ${RESOURCES.reputation} (Need: 40+)`,
      `🏛️ BLM Relations: ${RESOURCES.blmRelations} (Need: 40+)`,
      `💰 Funds: $${RESOURCES.funds.toLocaleString()} (Need: $0+)`
    ];
    
    const statsTexts = stats.map((stat, i) => {
      return this.add.text(0, 260 + i * 22, stat, {
        fontFamily: 'Source Sans 3, sans-serif',
        fontSize: '14px',
        color: '#cccccc'
      }).setOrigin(0.5);
    });
    
    // Tip based on reason
    const tips: Record<string, string> = {
      'extinction': '💡 Tip: Use breeding and protect your horses from harsh conditions',
      'low_pop': '💡 Tip: Balance PZP use with population growth needs',
      'starvation': '💡 Tip: Play forage cards and manage herd size',
      'drought': '💡 Tip: Invest in water infrastructure early',
      'overpop': '💡 Tip: Use PZP vaccines to control population growth',
      'disease': '💡 Tip: Play vet cards and maintain herd health',
      'reputation': '💡 Tip: Engage with public through tours and social media',
      'blm': '💡 Tip: Maintain good relations with BLM through partnerships',
      'bankrupt': '💡 Tip: Balance spending with fundraising activities',
      'timeout': '💡 Tip: Work faster to meet goals before 2030'
    };
    
    const tip = this.add.text(0, 420, tips[reason] || '💡 Tip: Try a different strategy!', {
      fontFamily: 'Georgia',
      fontSize: '14px',
      color: '#ffcc44',
      fontStyle: 'italic'
    }).setOrigin(0.5);
    
    // Buttons
    const restartBtn = this.add.rectangle(-80, 500, 140, 45, 0x44aa44)
      .setStrokeStyle(2, 0x66cc66)
      .setInteractive({ useHandCursor: true });
    const restartText = this.add.text(-80, 500, '🔄 Try Again', {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    const quitBtn = this.add.rectangle(80, 500, 140, 45, 0x666666)
      .setStrokeStyle(2, 0x888888)
      .setInteractive({ useHandCursor: true });
    const quitText = this.add.text(80, 500, '🚪 Main Menu', {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#cccccc'
    }).setOrigin(0.5);
    
    container.add([title, msg, statsBg, statsTitle, ...statsTexts, tip, restartBtn, restartText, quitBtn, quitText]);
    
    restartBtn.on('pointerover', () => restartBtn.setFillStyle(0x55bb55));
    restartBtn.on('pointerout', () => restartBtn.setFillStyle(0x44aa44));
    restartBtn.on('pointerdown', () => this.resetGame());
    
    quitBtn.on('pointerover', () => quitBtn.setFillStyle(0x777777));
    quitBtn.on('pointerout', () => quitBtn.setFillStyle(0x666666));
    quitBtn.on('pointerdown', () => this.resetGame());
  }
  
  private showPartialSuccess(): void {
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85);
    overlay.setDepth(2000);
    
    const container = this.add.container(GAME_WIDTH / 2, 0).setDepth(2001);
    
    // Check what's missing
    const issues: string[] = [];
    if (RESOURCES.population < AML_LOW) issues.push(`Population too low (${RESOURCES.population}/${AML_LOW})`);
    if (RESOURCES.population > AML_HIGH) issues.push(`Population too high (${RESOURCES.population}/${AML_HIGH})`);
    if (RESOURCES.diversity < 50) issues.push(`Diversity too low (${RESOURCES.diversity}%/50%)`);
    if (RESOURCES.herdHealth < 60) issues.push(`Herd health poor (${RESOURCES.herdHealth}%/60%)`);
    if (RESOURCES.reputation < 40) issues.push(`Reputation low (${RESOURCES.reputation}/40)`);
    if (RESOURCES.blmRelations < 40) issues.push(`BLM relations strained (${RESOURCES.blmRelations}/40)`);
    if (RESOURCES.funds < 0) issues.push(`In debt ($${RESOURCES.funds})`);
    
    // Title
    const title = this.add.text(0, 80, '⚠️ PARTIAL SUCCESS ⚠️', {
      fontFamily: 'Georgia',
      fontSize: '36px',
      color: '#ffaa44'
    }).setOrigin(0.5);
    
    const subtitle = this.add.text(0, 130, 'You reached 2030, but not all goals were met.', {
      fontFamily: 'Georgia',
      fontSize: '18px',
      color: '#cccccc'
    }).setOrigin(0.5);
    
    // Issues panel
    const issuesBg = this.add.rectangle(0, 240, 450, 120, 0x442222, 0.9)
      .setStrokeStyle(2, 0x884444);
    
    const issuesTitle = this.add.text(0, 190, '❌ Issues to Address:', {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#ff8888'
    }).setOrigin(0.5);
    
    const issueTexts = issues.slice(0, 4).map((issue, i) => {
      return this.add.text(0, 215 + i * 22, `• ${issue}`, {
        fontFamily: 'Source Sans 3, sans-serif',
        fontSize: '14px',
        color: '#ffaaaa'
      }).setOrigin(0.5);
    });
    
    // Score calculation
    let score = 0;
    if (RESOURCES.population >= AML_LOW && RESOURCES.population <= AML_HIGH) score += 25;
    if (RESOURCES.diversity >= 50) score += 20;
    if (RESOURCES.herdHealth >= 60) score += 20;
    if (RESOURCES.reputation >= 40) score += 15;
    if (RESOURCES.blmRelations >= 40) score += 10;
    if (RESOURCES.funds >= 0) score += 10;
    
    const scoreText = this.add.text(0, 340, `📊 Score: ${score}/100`, {
      fontFamily: 'Georgia',
      fontSize: '24px',
      color: score >= 70 ? '#88ff88' : (score >= 40 ? '#ffff88' : '#ff8888')
    }).setOrigin(0.5);
    
    const encouragement = score >= 70 
      ? '🌟 So close! One more try should do it!'
      : score >= 40 
        ? '💪 Good effort! Keep improving!'
        : '📚 Study the cards and try new strategies!';
    
    const encourageText = this.add.text(0, 380, encouragement, {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#aaaaaa',
      fontStyle: 'italic'
    }).setOrigin(0.5);
    
    // Buttons
    const retryBtn = this.add.rectangle(-80, 450, 140, 45, 0x44aa44)
      .setStrokeStyle(2, 0x66cc66)
      .setInteractive({ useHandCursor: true });
    const retryText = this.add.text(-80, 450, '🔄 Try Again', {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    const continueBtn = this.add.rectangle(80, 450, 140, 45, 0x4488cc)
      .setStrokeStyle(2, 0x66aaee)
      .setInteractive({ useHandCursor: true });
    const continueText = this.add.text(80, 450, '▶️ Continue', {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    container.add([title, subtitle, issuesBg, issuesTitle, ...issueTexts, scoreText, encourageText, retryBtn, retryText, continueBtn, continueText]);
    
    retryBtn.on('pointerover', () => retryBtn.setFillStyle(0x55bb55));
    retryBtn.on('pointerout', () => retryBtn.setFillStyle(0x44aa44));
    retryBtn.on('pointerdown', () => this.resetGame());
    
    // Continue lets them keep playing past 2030
    continueBtn.on('pointerover', () => continueBtn.setFillStyle(0x5599dd));
    continueBtn.on('pointerout', () => continueBtn.setFillStyle(0x4488cc));
    continueBtn.on('pointerdown', () => {
      container.destroy();
      overlay.destroy();
      // Give them more time
      RESOURCES.year = 2029; // Reset to before 2030 so they get another chance
    });
  }

  private showVictory(): void {
    // Play victory sound
    this.audioManager.playSFX('good_ending');
    
    // Unlock paint horses for future playthroughs!
    unlockPaint();
    
    // Check for HMA unlock
    const unlockedNew = this.checkHMAUnlock();
    
    const overlay = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.9);
    overlay.setDepth(2000);
    
    const container = this.add.container(GAME_WIDTH / 2, 0).setDepth(2001);
    
    // Celebration particles
    for (let i = 0; i < 30; i++) {
      const particle = this.add.text(
        Phaser.Math.Between(-350, 350),
        Phaser.Math.Between(50, 200),
        Phaser.Math.RND.pick(['⭐', '🐴', '✨', '🎉', '💖', '🌟', '🏆']),
        { fontSize: Phaser.Math.Between(20, 40) + 'px' }
      ).setDepth(2002);
      container.add(particle);
      this.tweens.add({
        targets: particle,
        y: particle.y + 300,
        alpha: 0,
        rotation: Phaser.Math.Between(-1, 1),
        duration: 3000,
        delay: i * 80
      });
    }
    
    // Title with glow effect
    const titleGlow = this.add.text(0, 65, '🏆 VICTORY! 🏆', {
      fontFamily: 'Georgia',
      fontSize: '44px',
      color: '#ffaa00'
    }).setOrigin(0.5).setAlpha(0.5);
    
    const title = this.add.text(0, 60, '🏆 VICTORY! 🏆', {
      fontFamily: 'Georgia',
      fontSize: '44px',
      color: '#ffd700'
    }).setOrigin(0.5);
    
    const subtitle = this.add.text(0, 110, 'Perfect Balance Achieved!', {
      fontFamily: 'Georgia',
      fontSize: '20px',
      color: '#88ff88'
    }).setOrigin(0.5);
    
    // Stats panel
    const statsBg = this.add.rectangle(0, 240, 420, 160, 0x1a3a1a, 0.9)
      .setStrokeStyle(2, 0x44aa44);
    
    const statsTitle = this.add.text(0, 175, '📊 Final Report', {
      fontFamily: 'Georgia',
      fontSize: '18px',
      color: '#88ff88'
    }).setOrigin(0.5);
    
    const stats = [
      `🐴 Herd: ${RESOURCES.population} mustangs (Goal: ${AML_LOW}-${AML_HIGH}) ✓`,
      `🧬 Diversity: ${RESOURCES.diversity}% ✓`,
      `❤️ Health: ${RESOURCES.herdHealth}% ✓`,
      `⭐ Reputation: ${RESOURCES.reputation} ✓`,
      `💰 Funds: $${RESOURCES.funds.toLocaleString()} ✓`
    ];
    
    const statsTexts = stats.map((stat, i) => {
      return this.add.text(0, 200 + i * 24, stat, {
        fontFamily: 'Source Sans 3, sans-serif',
        fontSize: '14px',
        color: '#aaffaa'
      }).setOrigin(0.5);
    });
    
    // Flavor text
    const flavor = this.add.text(0, 350, 
      'The Pryor Mountains wild horses thrive under your care!\nCloud\'s legacy lives on for generations.', {
      fontFamily: 'Georgia',
      fontSize: '14px',
      color: '#d4c4a4',
      align: 'center'
    }).setOrigin(0.5);
    
    // Unlocks section
    const unlockY = 410;
    const unlocks: Phaser.GameObjects.Text[] = [];
    
    unlocks.push(this.add.text(0, unlockY, '🎁 UNLOCKED:', {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#ffcc44'
    }).setOrigin(0.5));
    
    unlocks.push(this.add.text(0, unlockY + 25, '🎨 Paint horses now available!', {
      fontFamily: 'Source Sans 3, sans-serif',
      fontSize: '14px',
      color: '#ffaa44'
    }).setOrigin(0.5));
    
    if (unlockedNew) {
      unlocks.push(this.add.text(0, unlockY + 45, `🗺️ New HMA: ${unlockedNew}!`, {
        fontFamily: 'Source Sans 3, sans-serif',
        fontSize: '14px',
        color: '#44ff44'
      }).setOrigin(0.5));
    }
    
    // Buttons
    const playAgainBtn = this.add.rectangle(-90, 520, 150, 45, 0x44aa44)
      .setStrokeStyle(2, 0x66cc66)
      .setInteractive({ useHandCursor: true });
    const playAgainText = this.add.text(-90, 520, '🔄 Play Again', {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    const newHMABtn = this.add.rectangle(90, 520, 150, 45, 0x4488cc)
      .setStrokeStyle(2, 0x66aaee)
      .setInteractive({ useHandCursor: true });
    const newHMAText = this.add.text(90, 520, '🗺️ New HMA', {
      fontFamily: 'Georgia',
      fontSize: '16px',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    container.add([titleGlow, title, subtitle, statsBg, statsTitle, ...statsTexts, flavor, ...unlocks, 
                   playAgainBtn, playAgainText, newHMABtn, newHMAText]);
    
    playAgainBtn.on('pointerover', () => playAgainBtn.setFillStyle(0x55bb55));
    playAgainBtn.on('pointerout', () => playAgainBtn.setFillStyle(0x44aa44));
    playAgainBtn.on('pointerdown', () => this.resetGame());
    
    newHMABtn.on('pointerover', () => newHMABtn.setFillStyle(0x5599dd));
    newHMABtn.on('pointerout', () => newHMABtn.setFillStyle(0x4488cc));
    newHMABtn.on('pointerdown', () => this.resetGame()); // TODO: HMA selection
    
    // Achievement check
    this.checkAchievements();
  }

  private checkHMAUnlock(): string | null {
    // Unlock Kiger after first Pryor win
    if (!GAME_STATE.unlockedHMAs.includes('kiger') && GAME_STATE.currentHMA === 'pryor') {
      GAME_STATE.unlockedHMAs.push('kiger');
      return 'Kiger Mustang HMA';
    }
    // Unlock McCullough after Kiger win
    if (!GAME_STATE.unlockedHMAs.includes('mccullough') && GAME_STATE.currentHMA === 'kiger') {
      GAME_STATE.unlockedHMAs.push('mccullough');
      return 'McCullough Peaks HMA';
    }
    return null;
  }

  private checkAchievements(): void {
    const newAchievements: string[] = [];
    
    if (GAME_STATE.totalFoalsBorn >= 50 && !GAME_STATE.achievements.includes('cloud_heir')) {
      GAME_STATE.achievements.push('cloud_heir');
      newAchievements.push('🏅 Cloud\'s Heir - 50 foals born!');
    }
    
    if (RESOURCES.diversity >= 80 && !GAME_STATE.achievements.includes('geneticist')) {
      GAME_STATE.achievements.push('geneticist');
      newAchievements.push('🧬 Master Geneticist - 80% diversity!');
    }
    
    if (RESOURCES.funds >= 30000 && !GAME_STATE.achievements.includes('fundraiser')) {
      GAME_STATE.achievements.push('fundraiser');
      newAchievements.push('💰 Champion Fundraiser - $30k saved!');
    }
    
    // Display achievements
    newAchievements.forEach((ach, i) => {
      const achText = this.add.text(GAME_WIDTH / 2, 580 + i * 25, ach, {
        fontFamily: 'Georgia',
        fontSize: '14px',
        color: '#ffd700'
      }).setOrigin(0.5);
    });
  }

  private resetGame(): void {
    // Stop all audio before restarting to prevent double-playing
    this.audioManager.stopAllSounds();
    
    // Reset all resources to starting values
    RESOURCES.population = 30;
    RESOURCES.forage = 70;
    RESOURCES.water = 60;
    RESOURCES.funds = 8000;
    RESOURCES.year = 2025;
    RESOURCES.foalRate = 0.12;
    RESOURCES.diversity = 50;
    RESOURCES.reputation = 50;
    RESOURCES.herdHealth = 75;
    RESOURCES.blmRelations = 50;
    
    // Reset action system
    ACTION_SYSTEM.actionsRemaining = ACTION_SYSTEM.actionsPerYear;
    ACTION_SYSTEM.breedingCooldown = 0;
    ACTION_SYSTEM.cardsPlayedThisYear = 0;
    ACTION_SYSTEM.boughtCardsThisYear = false;
    
    // Reset game state but keep unlocks and achievements
    GAME_STATE.persistentDeck = [];
    GAME_STATE.discardPile = [];
    GAME_STATE.journalEntries = [];
    GAME_STATE.totalFoalsBorn = 0;
    GAME_STATE.yearsPlayed = 0;
    
    this.scene.restart();
  }
}
